const ON_CHANGE_DEBOUNCE_TIMER = 300;
const RECENTLY_VIEWED_KEY = "recently-viewed"; // 最近浏览

const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update", // 购物车更新
  quantityUpdate: "quantity-update", // 产品页面产品数量更新
  variantChange: "variant-change", // 产品页面产品变体变化
  cartError: "cart-error", // 购物车错误
};

const webvista = (function () {
  // 私有常量
  const SCROLL_LOCK_ATTR = "scroll-y-off";
  const FOCUS_VISIBLE_KEYS = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  const NO_DECIMAL_CURRENCIES = [
    "JPY",
    "KRW",
    "VND",
    "IDR",
    "CLP",
    "COP",
    "PYG",
    "UGX",
    "ISK",
    "HUF",
    "RWF",
    "BIF",
    "DJF",
    "GNF",
    "KMF",
    "XAF",
    "XOF",
    "XPF",
  ];

  // 私有变量
  const trapFocusHandlers = {};
  let currentFocusedElement = null;
  let mouseClick = true; // 默认认为是鼠标操作，避免初始聚焦就添加样式
  let subscribers = {};

  // 私有方法
  function focusVisiblePolyfill() {
    window.addEventListener("keydown", (event) => {
      if (FOCUS_VISIBLE_KEYS.includes(event.code.toUpperCase())) {
        mouseClick = false;
      }
    });

    window.addEventListener("mousedown", () => {
      mouseClick = true;
    });

    window.addEventListener(
      "focus",
      () => {
        if (currentFocusedElement && currentFocusedElement.classList) {
          currentFocusedElement.classList.remove("focused");
        }

        if (mouseClick) return;

        currentFocusedElement = document.activeElement;
        if (currentFocusedElement && currentFocusedElement.classList) {
          currentFocusedElement.classList.add("focused");
        }
      },
      true,
    );
  }

  function loadingImage(image) {
    if (image.hasAttribute("data-srcset")) {
      image.setAttribute("srcset", image.dataset.srcset);
      image.removeAttribute("data-srcset");
      image.onload = () => image.classList.add("image-lazy-loaded");
    } else if (!image.onload) {
      image.classList.add("image-lazy-loaded");
    }
  }

  function moveTooltip(event, tooltip) {
    tooltip.style.left = `${event.clientX}px`;
    tooltip.style.top = `${event.clientY - 16}px`;
  }

  /**
   * 计算元素可见比例
   */
  function percentageSeen(element) {
    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY;
    const elemTop = element.getBoundingClientRect().top + scrollY;
    const elemHeight = element.offsetHeight;

    if (elemTop > scrollY + viewportHeight) return 0;
    if (elemTop + elemHeight < scrollY) return 100;

    const distance = scrollY + viewportHeight - elemTop;
    return (distance / ((viewportHeight + elemHeight) / 100)).toFixed(2);
  }

  /**
   * 随机生成区间范围的数字
   * @param min
   * @param max
   * @returns {*}
   */
  function randomInRange(min, max) {
    return Math.random() * (max - min) + min;
  }

  return {
    /**
     * 防抖函数
     * @param {Function} fn 要执行的函数
     * @param {number} wait 等待毫秒数
     * @returns {Function} 防抖后的函数
     */
    debounce: function (fn, wait) {
      let timeoutId;
      return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    /**
     * 节流函数
     * @param {Function} fn 要执行的函数
     * @param {number} delay 延迟毫秒数
     * @returns {Function} 节流后的函数
     */
    throttle: function (fn, delay) {
      let lastCallTime = 0;
      return function (...args) {
        const now = Date.now();
        if (now - lastCallTime < delay) return;
        lastCallTime = now;
        return fn.apply(this, args);
      };
    },

    /**
     * 订阅事件
     * @param eventName
     * @param callback
     * @returns {(function(): void)|*}
     */
    subscribe: function (eventName, callback) {
      if (subscribers[eventName] === undefined) {
        subscribers[eventName] = [];
      }

      subscribers[eventName] = [...subscribers[eventName], callback];

      return () => {
        subscribers[eventName] = subscribers[eventName].filter(
          (cb) => cb !== callback,
        );
      };
    },

    /**
     * 发布事件
     * @param eventName
     * @param data
     */
    publish: function (eventName, data) {
      if (subscribers[eventName]) {
        subscribers[eventName].forEach((callback) => {
          callback(data);
        });
      }
    },

    /**
     * 获取fetch配置
     * @param {string} [type="json"] 响应类型
     * @param {string} [method="POST"] HTTP方法
     * @returns {Object} fetch配置对象
     */
    fetchConfig: function (type = "json", method = "POST") {
      return {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Accept: `application/${type}`,
        },
      };
    },

    /**
     * 内容请求
     * @param url
     * @param signal
     * @returns {Promise<Document>}
     */
    fetchHtml: async function (url, signal = null) {
      const fetchOptions = signal ? { signal } : {};
      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        throw new Error(
          `HTTP error! Status: ${response.status} ${response.statusText}`,
        );
      }

      const text = await response.text();
      return new DOMParser().parseFromString(text, "text/html");
    },

    /**
     * 判断是否为数字
     * @param {any} value 要检查的值
     * @returns {boolean} 是否为有效数字
     */
    isNumeric: function (value) {
      if (typeof value === "number" && !isNaN(value)) return true;
      if (typeof value === "string" && value.trim() !== "") {
        return !isNaN(Number(value));
      }
      return false;
    },

    /**
     * 转换为数字，失败则返回备用值
     * @param {*} value
     * @param {number} fallback
     * @returns {number}
     */
    toNumber: function (value, fallback = 0) {
      const n = typeof value === "number" ? value : Number(value);
      return Number.isFinite(n) ? n : fallback;
    },

    clampNumber: function (value, min, max) {
      return Math.min(Math.max(value, min), max);
    },

    /**
     * 格式化货币金额
     * @param {number} [amount=0] 金额(扩大100倍后的值)
     * @param {string} currency 货币代码
     * @returns {string|undefined} 格式化后的金额字符串
     */
    formatPriceAmount: function (amount = 0, currency) {
      if (!window["priceFormatTemplate"] || !currency) return;

      const value = amount / 100;
      const hasNoDecimals = NO_DECIMAL_CURRENCIES.includes(currency);

      const locale = currency === "EUR" ? "de-DE" : undefined;
      const price = value.toLocaleString(locale, {
        minimumFractionDigits: hasNoDecimals ? 0 : 2,
        maximumFractionDigits: hasNoDecimals ? 0 : 2,
      });

      return window["priceFormatTemplate"].replace(/0([,.]0{0,2})?/, price);
    },

    /**
     * 向下取整到指定倍数
     * @param {number} n 原始数值
     * @param {number} x 倍数
     * @returns {number} 取整后的值
     */
    floorToMultiple: function (n, x) {
      return Math.floor(n / x) * x;
    },

    /**
     * 向上取整到指定倍数
     * @param {number} n 原始数值
     * @param {number} x 倍数
     * @returns {number} 取整后的值
     */
    ceilToMultiple: function (n, x) {
      return Math.ceil(n / x) * x;
    },

    /**
     * 禁用页面滚动
     * 模拟页面滚动位置
     */
    disablePageScroll: function () {
      // 判断是否已经禁用
      if (document.body.hasAttribute(SCROLL_LOCK_ATTR)) return;

      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      document.body.style.top = -scrollTop + "px";
      document.body.setAttribute(SCROLL_LOCK_ATTR, "true");
    },

    /**
     * 重新启用页面滚动
     * 前提是页面滚动被disablePageScroll禁用
     */
    enablePageScroll: function () {
      if (!document.body.hasAttribute(SCROLL_LOCK_ATTR)) return;

      const scrollPosition = -parseInt(document.body.style.top, 10);
      document.body.style.top = null;
      document.body.removeAttribute(SCROLL_LOCK_ATTR);

      requestAnimationFrame(() => window.scrollTo(0, scrollPosition));
    },

    /**
     * 获取可聚焦元素
     */
    getFocusableElements: function (container, filter_invisible = true) {
      let list = Array.from(
        container.querySelectorAll(
          "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
        ),
      );

      if (filter_invisible) {
        list = list.filter((element) => element.offsetParent !== null);
      }

      return list;
    },

    /**
     * 设置焦点陷阱
     */
    trapFocus: function (container, elementToFocus) {
      if (!container) return;

      const trap = container.hasAttribute("data-trap")
        ? container
        : container.querySelector("[data-trap]");
      if (!trap) return;

      const elements = this.getFocusableElements(trap);
      if (elements.length <= 0) return;

      const first = elements[0];
      const last = elements[elements.length - 1];
      elementToFocus = elementToFocus || trap;

      this.removeTrapFocus();

      trapFocusHandlers.focusin = (event) => {
        if (
          event.target !== trap &&
          event.target !== last &&
          event.target !== first
        )
          return;
        document.addEventListener("keydown", trapFocusHandlers.keydown);
      };

      trapFocusHandlers.focusout = function () {
        document.removeEventListener("keydown", trapFocusHandlers.keydown);
      };

      trapFocusHandlers.keydown = function (event) {
        if (event.code && event.code.toUpperCase() !== "TAB") return;

        if (event.target === last && !event.shiftKey) {
          event.preventDefault();
          first.focus();
        } else if (
          (event.target === trap || event.target === first) &&
          event.shiftKey
        ) {
          event.preventDefault();
          last.focus();
        }
      };

      document.addEventListener("focusin", trapFocusHandlers.focusin);
      document.addEventListener("focusout", trapFocusHandlers.focusout);

      elementToFocus.focus();

      if (
        elementToFocus.tagName === "INPUT" &&
        ["search", "text", "email", "url"].includes(elementToFocus.type) &&
        elementToFocus.value
      ) {
        elementToFocus.setSelectionRange(
          elementToFocus.value.length,
          elementToFocus.value.length,
        );
      }
    },

    /**
     * 移除焦点陷阱
     */
    removeTrapFocus: function (elementToFocus = null) {
      document.removeEventListener("focusin", trapFocusHandlers.focusin);
      document.removeEventListener("focusout", trapFocusHandlers.focusout);
      document.removeEventListener("keydown", trapFocusHandlers.keydown);

      if (!elementToFocus) return;

      if (elementToFocus.closest("[data-trap]")) {
        this.trapFocus(elementToFocus.closest("[data-trap]"), elementToFocus);
      } else {
        elementToFocus.focus();
      }
    },

    /**
     * 初始化focus-visible polyfill
     */
    initFocusVisible: function () {
      try {
        document.querySelector(":focus-visible");
      } catch (e) {
        focusVisiblePolyfill();
      }
    },

    /**
     * 关闭所有视频播放
     * @param {HTMLElement|Document} dom 指定的Dom节点内
     */
    pauseAllMedia: function (dom = document) {
      dom.querySelectorAll(".js-youtube").forEach((video) => {
        video.contentWindow.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          "*",
        );
      });
      dom.querySelectorAll(".js-vimeo").forEach((video) => {
        video.contentWindow.postMessage('{"method":"pause"}', "*");
      });
      dom.querySelectorAll("video").forEach((video) => video.pause());

      dom.querySelectorAll("product-model").forEach((model) => {
        if (model.modelViewerUI) model.modelViewerUI.pause();
      });
    },

    /**
     * 播放所有视频
     * @param {HTMLElement|Document} [dom=document] - 指定的Dom节点内
     */
    playAllMedia: function (dom = document) {
      dom.querySelectorAll(".js-youtube").forEach((video) => {
        video.contentWindow.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          "*",
        );
      });
      dom.querySelectorAll(".js-vimeo").forEach((video) => {
        video.contentWindow.postMessage('{"method":"play"}', "*");
      });
      dom.querySelectorAll("video").forEach((video) => video.play());
    },

    /**
     * 监听ESCAPE按键关闭Details
     * @param {KeyboardEvent} event 键盘事件对象
     */
    onKeyUpEscape: function (event) {
      if (event.code && event.code.toUpperCase() !== "ESCAPE") return;

      const openDetailsElement = event.target.closest("details[open]");
      if (!openDetailsElement) return;

      const summaryElement = openDetailsElement.querySelector("summary");
      openDetailsElement.removeAttribute("open");
      summaryElement.setAttribute("aria-expanded", "false");
      summaryElement.focus();
    },

    /**
     * 设置Cookie
     * @param {string} name Cookie名称
     * @param {string} value Cookie值
     * @param {number} [hoursToExpire=30] 过期时间(小时)
     */
    setCookie: function (name, value, hoursToExpire = 30) {
      const date = new Date();
      date.setTime(date.getTime() + hoursToExpire * 60 * 60 * 1000);
      document.cookie = `${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/`;
    },

    /**
     * 获取Cookie值
     * @param {string} name Cookie名称
     * @returns {string|null} Cookie值
     */
    getCookie: function (name) {
      const nameEQ = name + "=";
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i];
        while (cookie.charAt(0) === " ") {
          cookie = cookie.substring(1);
        }
        if (cookie.indexOf(nameEQ) === 0) {
          return decodeURIComponent(cookie.substring(nameEQ.length));
        }
      }
      return null;
    },

    /**
     * 存储数据到LocalStorage
     * @param {string} key 存储键名
     * @param {any} value 存储值
     * @param {number|null} [expirationInMinutes=null] 过期时间(分钟)
     */
    storeData: function (key, value, expirationInMinutes = null) {
      const dataToStore = {
        value: value,
        expiration: expirationInMinutes
          ? Date.now() + expirationInMinutes * 60000
          : null,
      };
      localStorage.setItem(key, JSON.stringify(dataToStore));
    },

    /**
     * 从LocalStorage读取数据
     * @param {string} key 存储键名
     * @returns {any|null} 存储的值或null
     */
    retrieveData: function (key) {
      try {
        const data = localStorage.getItem(key);
        if (!data) return null;

        const dataObj = JSON.parse(data);
        if (!dataObj) return null;

        if (dataObj.expiration === null || Date.now() < dataObj.expiration) {
          return dataObj.value;
        }

        localStorage.removeItem(key);
        return null;
      } catch (e) {
        return null;
      }
    },

    /**
     * 异步等待
     * @param {number} ms 等待毫秒数
     * @returns {Promise<void>}
     */
    sleep: function (ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    /**
     * 数字补零
     * @param {number} num 原始数字
     * @param {number} length 补零后的长度
     * @returns {string} 补零后的字符串
     */
    padNumber: function (num, length) {
      return String(num).padStart(length, "0");
    },

    /**
     * 规范化字符串
     * 1. 使用 'NFD' 方式分解字符
     * 2. 移除所有变音符号
     * 3. 转换为小写
     * @param {string} str 输入字符串
     * @returns {string} 规范化后的字符串
     */
    normalizeString: function (str) {
      return str
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toLowerCase();
    },

    /**
     * 检测移动端屏幕
     * @returns {boolean} 是否是移动端(≤749px)
     */
    isMobileScreen: function () {
      return window.matchMedia("(max-width: 749px)").matches;
    },

    /**
     * 检测平板屏幕
     * @returns {boolean} 是否是平板(≤999px)
     */
    isPadScreen: function () {
      return window.matchMedia("(max-width: 999px)").matches;
    },

    /**
     * 是否RTL布局
     * @returns
     */
    isRTL: function () {
      return getComputedStyle(document.documentElement).direction === "rtl";
    },

    /**
     * 初始化图片懒加载
     */
    initLazyImages: function () {
      if (!window["enableLazyImage"]) return;

      const lazyImages = document.querySelectorAll(
        ".image-lazy-loading:not(.image-lazy-loaded)",
      );

      if ("IntersectionObserver" in window) {
        const observer = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                loadingImage(entry.target);
                observer.unobserve(entry.target);
              }
            });
          },
          { rootMargin: "0px 0px 400px 0px" },
        );
        lazyImages.forEach((img) => observer.observe(img));
      } else {
        lazyImages.forEach(loadingImage);
      }
    },

    /**
     * 初始化ToolTip
     */
    initTooltips: function () {
      if (this.isMobileScreen()) return;

      const triggers = document.querySelectorAll(
        '[data-toggle="tooltip"]:not([data-tooltip-loaded])',
      );

      const loadTooltip = (trigger) => {
        trigger.addEventListener("mouseenter", () => {
          // 先删除之前的tooltip
          document
            .querySelectorAll(".tool-tip")
            .forEach((element) => element.remove());
          // 新建
          const tooltip = document.createElement("div");
          tooltip.className = "tool-tip";
          tooltip.textContent = trigger.getAttribute("data-title");
          document.body.appendChild(tooltip);

          const onMouseMove = (e) => moveTooltip(e, tooltip);
          const hide = () => {
            tooltip.remove();
            trigger.removeEventListener("mousemove", onMouseMove);
          };

          trigger.addEventListener("mousemove", onMouseMove);
          trigger.addEventListener("mouseleave", hide, { once: true });
          window.addEventListener("scroll", hide, { once: true });
        });
        trigger.setAttribute("data-tooltip-loaded", "true");
      };

      if ("IntersectionObserver" in window) {
        const observer = new IntersectionObserver((entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              loadTooltip(entry.target);
              observer.unobserve(entry.target);
            }
          });
        });
        triggers.forEach((t) => observer.observe(t));
      } else {
        triggers.forEach(loadTooltip);
      }
    },

    /**
     * 初始化滚动协同效果
     */
    initScrollSynergy: function () {
      if (
        window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
        this.isMobileScreen()
      )
        return;

      const elements = Array.from(
        document.querySelectorAll(
          ".scroll-synergy:not([data-scroll-synergy-observed])",
        ),
      );

      if (elements.length <= 0) return;

      const elementStates = new WeakMap();

      // 初始化元素状态
      elements.forEach((el) => {
        elementStates.set(el, { prePosition: null, isVisible: false });
      });

      const updateElement = (el) => {
        const percentage = percentageSeen(el);
        const state = elementStates.get(el);
        let position;

        if (percentage <= 0) {
          position = 0;
        } else if (percentage < 30) {
          position = 1;
        } else if (percentage < 100) {
          position = 2;
        } else {
          position = 3;
        }

        // 位置发生改变
        if (state.prePosition !== position) {
          el.classList.remove("synergy-position--1", "synergy-position--2");

          if (position > 0 && position < 3) {
            el.classList.add(`synergy-position--${position}`);
          }

          state.prePosition = position;
        }

        el.style.setProperty("--synergy-ratio", `${percentage}%`);

        if (el.classList.contains("synergy--zoom-in")) {
          el.style.setProperty(
            "--zoom-in-ratio",
            (1 + 0.005 * percentage).toString(),
          );
        } else if (el.classList.contains("synergy--parallax")) {
          el.style.setProperty(
            "--parallax-ratio",
            (percentage / 100).toString(),
          );
        } else if (
          el.classList.contains("synergy--crab-left") ||
          el.classList.contains("synergy--crab-right")
        ) {
          let crabRatio = 0.25 * percentage;
          if (el.classList.contains("synergy--crab-left")) {
            crabRatio *= -1;
          }
          el.style.setProperty("--crab-ratio", `${crabRatio}%`);
        }
      };

      const handleScroll = this.throttle(() => {
        elements.forEach((el) => {
          const state = elementStates.get(el);
          if (state.isVisible) {
            updateElement(el);
          }
        });
      }, 20); // 可以根据需求调节节流时间

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          const state = elementStates.get(el);
          state.isVisible = entry.isIntersecting;

          if (entry.isIntersecting) {
            // 添加初始位置信息
            updateElement(el);
            el.setAttribute("data-init", "true");
          }
        });
      });

      elements.forEach((element) => {
        observer.observe(element);
        element.setAttribute("data-scroll-synergy-observed", "true");
      });
      window.addEventListener("scroll", handleScroll);
    },

    /**
     * 初始化标题闪烁
     */
    initTitleFlash: function () {
      // 存储原始标题和定时器
      let originalTitle = document.title;
      let alertInterval = null;

      const alertMessage =
        window["accessibilityStrings"]["stillHere"] || "We are still here!";

      document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
          // 用户离开时开始闪烁
          let counter = 0;
          originalTitle = document.title; // 保存当前标题（防止中途被修改）
          alertInterval = setInterval(() => {
            document.title = counter++ % 2 === 0 ? alertMessage : originalTitle;
          }, 5000);
        } else {
          // 用户返回时立即停止并恢复标题
          if (alertInterval) {
            clearInterval(alertInterval);
            alertInterval = null;
            document.title = originalTitle;
          }
        }
      });

      // 页面卸载时清理定时器（防止内存泄漏）
      window.addEventListener("beforeunload", () => {
        if (alertInterval) clearInterval(alertInterval);
      });
    },

    /**
     * 初始化Header高度
     */
    initHeaderHeight: function () {
      const header = document.querySelector("header");
      if (header) {
        const rect = header.getBoundingClientRect();
        document.documentElement.style.setProperty(
          "--header-height",
          `${Math.floor(rect.height)}px`,
        );
        document.documentElement.style.setProperty(
          "--header-bottom",
          `${Math.floor(rect.bottom)}px`,
        );
      }
    },

    /**
     * 弹出消息提示toast
     * @param {string} [message=""] 提示消息
     * @param {string} [type="info"] 类型(info|success|warning|error)
     */
    popToast: function (message = "", type = "info") {
      if (!message) return;

      const toast = document.createElement("div");
      toast.className = `toast type-${type}`;
      toast.setAttribute("aria-role", "alert");
      toast.setAttribute("aria-live", "assertive");
      toast.textContent = message;

      setTimeout(() => toast.remove(), 5000);

      const container = document.getElementById("toasts-container");
      if (container) container.appendChild(toast);
    },

    /**
     * 滚动到指定元素位置
     * @param {HTMLElement} element 目标元素
     * @param {number} [offset=50] 距离顶部的偏移量
     */
    scrollToElementWithOffset: function (element, offset = 50) {
      if (!element) return;

      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      const targetPosition = Math.max(0, elementTop - offset);

      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    },

    /**
     * 滚动到页眉底部位置
     * @param {HTMLElement} element 目标元素
     * @param {number} [offset=0] 额外偏移量
     */
    scrollElementToHeaderBottom: function (element, offset = 0) {
      const header = document.getElementById("Page-Header");
      if (!header || !element) return;

      const headerBottom = header.getBoundingClientRect().bottom;
      this.scrollToElementWithOffset(
        element,
        Math.max(0, headerBottom) + offset,
      );
    },

    /**
     * Confetti effects
     * @param particleRatio
     * @param opts
     */
    confetti: {
      // Cached SVG shapes (lazy-initialized on first use)
      _shapes: null,

      // Build and cache all SVG shapes used by confetti effects
      getShapes: function () {
        if (this._shapes) return this._shapes;
        if (typeof confetti === "undefined") return null;

        this._shapes = {
          star: confetti.shapeFromPath({
            path: "M43.767 59.6698L31.4327 53.1854L19.094 59.6698C14.3183 62.187 8.68819 58.1198 9.60225 52.7741L11.9573 39.0363L1.97805 29.3081C-1.89655 25.5351 0.242508 18.9273 5.60265 18.1492L19.396 16.1429L25.5655 3.64453C27.971 -1.22187 34.901 -1.20781 37.2995 3.64453L43.467 16.1429L57.2604 18.1492C62.6135 18.9265 64.7643 25.5308 60.885 29.3081L50.9057 39.0363L53.2608 52.7741C54.1737 58.1143 48.5592 62.1831 43.767 59.6698Z",
            matrix: [
              0.03597122302158273, 0, 0, 0.03597122302158273,
              -4.856115107913669, -5.071942446043165,
            ],
          }),
          heart: confetti.shapeFromPath({
            path: "M167 72c19,-38 37,-56 75,-56 42,0 76,33 76,75 0,76 -76,151 -151,227 -76,-76 -151,-151 -151,-227 0,-42 33,-75 75,-75 38,0 57,18 76,56z",
            matrix: [
              0.03333333333333333, 0, 0, 0.03333333333333333,
              -5.566666666666666, -5.533333333333333,
            ],
          }),
          shortRibbon: confetti.shapeFromPath({
            path: "M13.4189 115.023L5.75487 107.359C-1.91777 99.6915 -1.91777 87.277 5.75487 79.6048L13.4189 71.9408C15.9681 69.3892 15.9681 65.2392 13.4166 62.6876L5.75448 55.0236C-1.91816 47.3556 -1.91816 34.9412 5.75448 27.269L16.0021 17.0213C17.9951 15.026 18.4873 11.9913 17.2267 9.46783C15.6127 6.23737 16.9224 2.30808 20.1548 0.692064C23.3787 -0.922386 27.3162 0.38269 28.9302 3.61979C32.7122 11.1862 31.2345 20.2901 25.2521 26.2717L15.0056 36.5193C12.4435 39.0861 12.4525 43.217 15.0056 45.7701L22.6697 53.4365C30.3212 61.0904 30.3212 73.5419 22.6697 81.1911L15.0056 88.8552C12.4435 91.422 12.4525 95.5528 15.0056 98.1059L22.6697 105.772C25.2251 108.325 25.2251 112.47 22.6697 115.023C20.1138 117.579 15.974 117.579 13.4189 115.023Z",
          }),
          longRibbon: confetti.shapeFromPath({
            path: "M15.0039 141.17C13.739 142.435 13.0843 144.049 13.0843 145.793C13.0843 147.538 13.7386 149.195 15.0039 150.416L15.4402 150.896L22.6363 158.092C30.3117 165.768 30.3117 178.198 22.6363 185.874C21.3714 187.139 19.714 187.793 18.0136 187.793C17.141 187.793 16.2687 187.618 15.4398 187.226C14.6988 186.921 14 186.485 13.3898 185.874C10.8597 183.301 10.8597 179.158 13.3898 176.584C15.9629 174.055 15.9629 169.911 13.3898 167.338L5.75741 159.706C2.05 155.999 0 151.07 0 145.793C0 140.559 2.05 135.631 5.75741 131.924L13.3898 124.248C15.9629 121.718 15.9629 117.575 13.3898 115.002L5.75741 107.369C2.05 103.662 0 98.7338 0 93.4565C0 88.2229 2.05 83.2944 5.75741 79.5873L13.3898 71.9112C15.9629 69.3815 15.9629 65.2382 13.3898 62.6651L5.75741 55.0327C2.05 51.3257 0 46.3972 0 41.1198C0 35.8863 2.05 30.9577 5.75741 27.2507L15.4402 17.5695L15.9636 17.0027C17.9703 14.9965 18.4492 11.9871 17.2281 9.4574C15.6148 6.23006 16.9238 2.30468 20.1511 0.691013C23.3785 -0.922656 27.3035 0.386326 28.9175 3.61327C32.7117 11.1586 31.2285 20.2738 25.2535 26.2488L15.0043 36.498C13.7394 37.7628 13.0847 39.3765 13.0847 41.121C13.0847 42.8655 13.739 44.523 15.0043 45.744L15.4406 46.2237L22.6367 53.4198C23.073 53.8561 23.5527 54.3358 23.9882 54.9026C30.2691 62.6221 29.8328 74.0049 22.6367 81.2018L15.0043 88.8342C13.7394 90.099 13.0847 91.7127 13.0847 93.4572C13.0847 95.2018 13.739 96.8592 15.0043 98.0803L15.4406 98.56L22.6367 105.756C30.312 113.432 30.312 125.862 22.6367 133.538L15.4406 140.734L15.0039 141.17Z",
          }),
        };
        return this._shapes;
      },

      fire: function (particleRatio, opts) {
        const count = 200;
        const defaults = {
          origin: { y: 0.7 },
          zIndex: 999,
          disableForReducedMotion: true,
        };

        confetti({
          ...defaults,
          ...opts,
          particleCount: Math.floor(count * particleRatio),
        });
      },

      cannonConfetti: function () {
        if (typeof confetti === "undefined") return;

        this.fire(0.25, {
          spread: 26,
          startVelocity: 55,
        });
        this.fire(0.2, {
          spread: 60,
        });
        this.fire(0.35, {
          spread: 100,
          decay: 0.91,
          scalar: 0.8,
        });
        this.fire(0.1, {
          spread: 120,
          startVelocity: 25,
          decay: 0.92,
          scalar: 1.2,
        });
        this.fire(0.1, {
          spread: 120,
          startVelocity: 45,
        });
      },

      firework: function () {
        if (typeof confetti === "undefined") return;
        const duration = 1.5 * 1000;
        const animationEnd = Date.now() + duration;
        const defaults = {
          startVelocity: 30,
          spread: 360,
          ticks: 60,
          zIndex: 999,
          disableForReducedMotion: true,
        };

        const interval = setInterval(function () {
          const timeLeft = animationEnd - Date.now();

          if (timeLeft <= 0) {
            return clearInterval(interval);
          }

          const particleCount = 150 * (timeLeft / duration);
          // Since particles fall down, start a bit higher than random
          confetti({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          });
          confetti({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          });
        }, 250);
      },

      // Side cannons shooting toward center (merged from 4 calls to 2 per frame)
      sideConfetti: function () {
        if (typeof confetti === "undefined") return;
        const shapes = this.getShapes();
        if (!shapes) return;

        const end = Date.now() + 1000;
        const defaults = {
          particleCount: 3,
          startVelocity: 80,
          spread: 55,
          colors: ["#ffff00", "#bb0000", "#ffffff"],
          disableForReducedMotion: true,
        };

        (function frame() {
          // Left side — include both default shapes and custom shapes in one call
          confetti({
            ...defaults,
            particleCount: 6,
            angle: 60,
            origin: { x: 0 },
            shapes: ["square", "circle", shapes.star, shapes.heart],
            scalar: randomInRange(1, 1.5),
          });

          // Right side — same merged approach
          confetti({
            ...defaults,
            particleCount: 6,
            angle: 120,
            origin: { x: 1 },
            shapes: ["square", "circle", shapes.star, shapes.heart],
            scalar: randomInRange(1, 1.5),
          });

          if (Date.now() < end) {
            requestAnimationFrame(frame);
          }
        })();
      },

      // Falling ribbons
      fallingRibbons: function () {
        if (typeof confetti === "undefined") return;
        const shapes = this.getShapes();
        if (!shapes) return;

        const duration = 3 * 1000;
        const animationEnd = Date.now() + duration;
        let skew = 1;

        // Color palette for random selection
        const colors = ["#ffffff", "#e9fa01", "#f93963", "#0b55e8", "#7c0e8f"];

        // Hoisted defaults — only ticks and colors change per frame
        const baseDefaults = {
          particleCount: 1,
          startVelocity: 0,
          zIndex: 999,
          disableForReducedMotion: true,
        };

        (function frame() {
          const timeLeft = animationEnd - Date.now();
          const ticks = Math.max(200, 500 * (timeLeft / duration));
          skew = Math.max(0.8, skew - 0.001);

          const color = colors[Math.floor(Math.random() * colors.length)];

          confetti({
            ...baseDefaults,
            ticks: ticks,
            colors: [color],
            origin: {
              x: Math.random(),
              y: Math.random() * skew - 0.2,
            },
            gravity: randomInRange(0.2, 0.4),
          });

          confetti({
            ...baseDefaults,
            ticks: ticks,
            colors: [color],
            origin: {
              x: Math.random(),
              // Since particles fall down, skew start toward the top
              y: 0,
            },
            shapes: [
              shapes.shortRibbon,
              shapes.longRibbon,
              shapes.heart,
              shapes.star,
            ],
            gravity: randomInRange(0.4, 0.6),
            scalar: randomInRange(1.5, 2),
            drift: randomInRange(-0.4, 0.4),
          });

          if (timeLeft > 0) {
            requestAnimationFrame(frame);
          }
        })();
      },

      // Falling snowflakes
      fallingSnowflakes: function (seconds = 15) {
        if (typeof confetti === "undefined") return;
        const duration = seconds * 1000;
        const animationEnd = Date.now() + duration;
        let skew = 1;

        (function frame() {
          const timeLeft = animationEnd - Date.now();
          const ticks = Math.max(200, 500 * (timeLeft / duration));
          skew = Math.max(0.8, skew - 0.001);

          confetti({
            particleCount: 1,
            startVelocity: 0,
            colors: ["#FFFFFF"],
            zIndex: 999,
            origin: {
              x: Math.random(),
              y: Math.random() * skew - 0.2,
            },
            shapes: ["circle"],
            gravity: randomInRange(0.3, 0.4),
            drift: randomInRange(-0.2, 0.2), // random horizontal drift
            scalar: randomInRange(0.6, 0.8),
            ticks: ticks,
            disableForReducedMotion: true,
          });

          if (timeLeft > 0) {
            requestAnimationFrame(frame);
          }
        })();
      },

      // Free shipping celebration event listener
      setupFreeShippingCelebration: function () {
        let firstTrigger = true,
          hasConfetti = false;

        document.addEventListener("freeShippingUnlocked", (event) => {
          const status = event.detail.status;

          if (status) {
            if (!hasConfetti && !firstTrigger) {
              this.fire(0.5, {
                spread: 60,
                angle: 90,
              });
            }
            hasConfetti = true;
          } else {
            hasConfetti = false;
          }

          firstTrigger = false;
        });
      },
    },

    /**
     * 颜色处理相关
     */
    hexToHSL: function (hex) {
      // 移除 # 号并解析 RGB
      hex = hex.replace(/^#/, "");
      const r = parseInt(hex.slice(0, 2), 16) / 255;
      const g = parseInt(hex.slice(2, 4), 16) / 255;
      const b = parseInt(hex.slice(4, 6), 16) / 255;

      // 计算最大值、最小值和差值
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const d = max - min; // 修复：明确定义 d

      let h = 0,
        s = 0,
        l = (max + min) / 2;

      // 计算色相（hue）
      if (d !== 0) {
        switch (max) {
          case r:
            h = ((g - b) / d) % 6;
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
        }
        h = Math.round(h * 60);
        if (h < 0) h += 360;
      }

      // 计算饱和度（saturation）
      if (d !== 0) {
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        s = Math.round(s * 100);
      }

      // 亮度（lightness）
      l = Math.round(l * 100);

      return { h, s, l };
    },

    HSLToHex: function (h, s, l) {
      // 归一化 HSL 值
      h /= 360;
      s /= 100;
      l /= 100;

      let r, g, b;

      if (s === 0) {
        r = g = b = l; // 灰度
      } else {
        const hue2rgb = (p, q, t) => {
          if (t < 0) t += 1;
          if (t > 1) t -= 1;
          if (t < 1 / 6) return p + (q - p) * 6 * t;
          if (t < 1 / 2) return q;
          if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
          return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;

        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
      }

      // RGB 分量转 HEX
      const toHex = (x) => {
        const hex = Math.round(x * 255).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      };

      return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
    },

    /**
     * 获取中间颜色插值
     * @param color1
     * @param color2
     * @param rate 0-1
     */
    getIntermediateColor: function (color1, color2, rate) {
      const hsl1 = webvista.hexToHSL(color1);
      const hsl2 = webvista.hexToHSL(color2);

      const h = hsl1.h + (hsl2.h - hsl1.h) * rate;
      const s = hsl1.s + (hsl2.s - hsl1.s) * rate;
      const l = hsl1.l + (hsl2.l - hsl1.l) * rate;

      return webvista.HSLToHex(h, s, l);
    },

    /**
     * Summary 内的菜单链接使用 span，通过 JS 进行跳转
     */
    initSummaryMenuLinks: function () {
      if (document.documentElement.dataset.summaryLinkInit) return;
      document.documentElement.dataset.summaryLinkInit = "true";

      document.addEventListener("click", (event) => {
        const trigger = event.target.closest(".js-summary-link");
        if (!trigger) return;

        const href = trigger.dataset.href;
        if (!href) return;
        const target = trigger.dataset.target;

        event.preventDefault();
        event.stopPropagation();
        if (target === "_blank") {
          window.open(href, "_blank", "noopener");
          return;
        }
        window.location.assign(href);
      });

      document.addEventListener("keydown", (event) => {
        const isActivationKey =
          event.key === "Enter" || event.key === " " || event.code === "Space";
        if (!isActivationKey) return;

        const trigger = event.target.closest(".js-summary-link");
        if (!trigger) return;

        const href = trigger.dataset.href;
        if (!href) return;
        const target = trigger.dataset.target;

        event.preventDefault();
        event.stopPropagation();
        if (target === "_blank") {
          window.open(href, "_blank", "noopener");
          return;
        }
        window.location.assign(href);
      });
    },

    /**
     * 初始化方法
     */
    init: function () {
      if (window.enableBrowserTabTitleFlash) this.initTitleFlash();
      this.initHeaderHeight();
      this.initLazyImages();
      this.initTooltips();
      this.initScrollSynergy();
      this.initFocusVisible();
      this.initSummaryMenuLinks();
      this.confetti.setupFreeShippingCelebration();

      // Festival effect: falling snow
      if (
        window.enableFestivalEffect &&
        window.festivalType == "christmas" &&
        window.enableChristmasSnow
      ) {
        const snow_duration = window.snowDuration || 30;
        this.confetti.fallingSnowflakes(snow_duration);
      }

      if (window.Shopify.designMode || window.debug) {
        // In design/debug mode, listen for viewport breakpoint changes
        const mediaQuery = window.matchMedia("(min-width: 750px)");

        const handleMediaChange = (event) => {
          // Re-enable on desktop
          if (event.matches) this.initScrollSynergy();
        };

        // Add listener
        mediaQuery.addEventListener("change", handleMediaChange);
      }
    },
  };
})();

// 自动初始化
document.addEventListener("DOMContentLoaded", () => webvista.init());

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (let i = 0, count = selector.options.length; i < count; i++) {
    const option = selector.options[i];
    if (value === option.value || value === option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

/**
 * 创建并提交一个表单以发送 POST 请求。
 * 使用此函数可以动态生成和提交表单，
 * 用于发送数据到服务器，适用于不通过<a>标签或JavaScript直接发送POST请求的场景。
 * @param path
 * @param options
 */
Shopify.postLink = function (path, options) {
  options = options || {};
  const method = options["method"] || "post";
  const params = options["parameters"] || {};

  const form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (let key in params) {
    const hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options,
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);

  if (options && options["hideElement"]) {
    this.provinceContainer = document.getElementById(options["hideElement"]);
  } else {
    this.provinceContainer = document.getElementById(province_domid);
  }

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this),
  );
  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    const value = this.countryEl.getAttribute("data-default");

    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    const value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    const opt = this.countryEl.options[this.countryEl.selectedIndex];
    const raw = opt.getAttribute("data-provinces");
    const provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length <= 0) {
      this.provinceContainer.style.display = "none";
    } else {
      provinces.forEach((province) => {
        const opt = document.createElement("option");
        opt.value = province[0];
        opt.innerHTML = province[1];
        this.provinceEl.appendChild(opt);
      });

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    if (values.length && values.length > 0)
      values.forEach((value) => {
        const opt = document.createElement("option");
        opt.value = value;
        opt.innerHTML = value;
        selector.appendChild(opt);
      });
  },
};

/**
 * Class SectionDynamicUpdate
 * 用于动态更新页面上的特定部分的内容。
 */
class SectionDynamicUpdate {
  /**
   * 解析提供的 HTML 字符串并返回指定选择器的元素的内部 HTML。
   *
   * @param {string} html - 要解析的 HTML 字符串。
   * @param id
   * @param {string} selector - 用于选取 HTML 元素的 CSS 选择器。
   * @returns {string} 匹配元素的内部 HTML，如果没有匹配的元素，则返回空字符串。
   */
  static getSectionInnerHTML(html, id, selector) {
    const dom = new DOMParser().parseFromString(html, "text/html");
    if (!dom) return "";

    const element = dom.querySelector(selector) || dom;
    return element.innerHTML;
  }

  /**
   * 根据提供的 sections 数组和响应内容，更新页面上的相应部分。
   *
   * @param {Array} sections - 包含部分信息的数组，每个部分包括 id, selector 和 section 属性。
   * @param {Object} responseSections - 包含新 HTML 内容的对象，键是 section 名称。
   */
  static updateSections(sections, responseSections) {
    sections.forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id)?.querySelector(section.selector) ||
        document.getElementById(section.id);

      if (elementToReplace)
        elementToReplace.innerHTML = SectionDynamicUpdate.getSectionInnerHTML(
          responseSections[section.section],
          section.id,
          section.selector,
        );
    });

    webvista.initLazyImages();
    if (typeof initializeScrollAnimationTrigger === "function") {
      initializeScrollAnimationTrigger();
    }
    webvista.initTooltips();
  }
}

// 商品数量选择器
class QuantityInput extends HTMLElement {
  quantityUpdateUnsubscriber = undefined;

  constructor() {
    super();
    this.changeEvent = new Event("change", { bubbles: true });

    this.input = this.querySelector("input"); // 输入框
    this.input.addEventListener("change", this.onInputChange.bind(this));

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this)),
    );

    this.updateQtyButtonState();
    this.quantityUpdateUnsubscriber = webvista.subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.updateQtyButtonState.bind(this),
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber();
  }

  /**
   * 处理输入框变化
   * @param event
   */
  onInputChange(event) {
    this.updateQtyButtonState();
  }

  /**
   * 处理加减按钮点击
   * @param event
   */
  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    // stepUp 和 stepDown 不会触发输入框的Change事件，所以需要手动触发
    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);
  }

  /**
   * 调整数量加减按钮 disabled 属性
   */
  updateQtyButtonState() {
    const value = parseInt(this.input.value);

    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity-button[name='minus']");
      buttonMinus.classList.toggle("disabled", value <= min);
    }

    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity-button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}
customElements.define("quantity-input", QuantityInput);

class HeaderDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetails = this.querySelector("details");
    this.mainSummary = this.querySelector("summary");
    this.header = this.closest(".section-header");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));

    // 抽屉打开
    this.mainDetails.addEventListener("toggle", this.onToggleMain.bind(this));

    // 子菜单
    const menuNav = this.querySelector(".menu-drawer-nav");
    menuNav.querySelectorAll("details").forEach((detail) => {
      detail.addEventListener("toggle", this.onToggleSub.bind(this));
    });
  }

  // 键盘事件
  onKeyUp(event) {
    if (event.code && event.code.toUpperCase() !== "ESCAPE") return;

    this.mainDetails.removeAttribute("open");
  }

  onToggleMain(event) {
    if (event.currentTarget.hasAttribute("open")) {
      this.openMenuDrawer();
    } else {
      this.closeMenuDrawer();
    }
  }

  onToggleSub(event) {
    if (event.currentTarget.hasAttribute("open")) {
      this.openSubmenu(event.currentTarget);
    } else {
      this.closeSubmenu(event.currentTarget);
    }
  }

  /**
   * 打开主菜单
   */
  openMenuDrawer() {
    setTimeout(() => {
      this.mainDetails.classList.add("menu-open");
    });
    this.mainSummary.setAttribute("aria-expanded", "true");
    if (this.header) this.header.classList.add("header--is-active");

    this.setTopPosition();
    if (window.Shopify.designMode) {
      window.addEventListener("resize", this.setTopPosition.bind(this));
    }

    webvista.disablePageScroll(); // 禁用滚动
  }

  /**
   * 关闭主菜单
   */
  closeMenuDrawer() {
    this.mainDetails.classList.remove("menu-open");
    this.mainSummary.setAttribute("aria-expanded", "false");
    if (this.header) this.header.classList.remove("header--is-active");

    // 关闭子菜单
    this.mainDetails
      .querySelectorAll('details[open][data-belong="1"]')
      .forEach((elements) => {
        elements.removeAttribute("open");
      });

    webvista.enablePageScroll(); // 启用滚动
    window.removeEventListener("resize", this.setTopPosition.bind(this));
  }

  // 失去焦点，关闭菜单
  onFocusOut(event) {
    const triggerElement = event.target;

    setTimeout(() => {
      const activeEl = document.activeElement;
      // activeEl === document.body 是 shadow DOM 焦点转移的过渡状态，不应触发关闭
      if (
        this.mainDetails.hasAttribute("open") &&
        activeEl !== document.body &&
        !this.mainDetails.contains(activeEl) &&
        !triggerElement.classList.contains("nav-button")
      ) {
        this.mainDetails.removeAttribute("open");
      }
    });
  }

  /**
   * 打开子菜单
   * @param detailsElement
   */
  openSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".has-submenu"); // 父级菜单
    const summaryElement = detailsElement.querySelector("summary");

    summaryElement.setAttribute("aria-expanded", "true");

    // 延迟添加，防止动画未应用到元素上
    setTimeout(() => {
      detailsElement.classList.add("menu-open");
    });

    if (parentMenuElement) {
      // 父菜单状态，显示有打开的子菜单
      parentMenuElement.classList.add("submenu-open");

      // 关闭兄弟菜单
      if (!webvista.isPadScreen()) {
        Array.from(
          parentMenuElement.querySelectorAll(
            `details[open][data-belong="${parentMenuElement.dataset.level}"]`,
          ),
        )
          .filter((details) => details !== detailsElement)
          .forEach((details) => {
            details.removeAttribute("open");
          });
      }
    }
  }

  /**
   * 关闭子菜单
   * @param detailsElement
   */
  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    const summaryElement = detailsElement.querySelector("summary");

    parentMenuElement && parentMenuElement.classList.remove("submenu-open");

    detailsElement.classList.remove("menu-open");
    summaryElement.setAttribute("aria-expanded", "false");

    // 关闭子菜单
    Array.from(detailsElement.querySelectorAll("details[open]"), (details) => {
      details.removeAttribute("open");
    });

    Array.from(detailsElement.querySelectorAll(".submenu-open"), (element) => {
      element.classList.remove("submenu-open");
    });
  }

  /**
   * 设置菜单抽屉顶部位置
   */
  setTopPosition() {
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom",
      )
        ? 1
        : 0; // 判断Header是否有border-bottom

    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom",
        `${this.header.querySelector("header").getBoundingClientRect().bottom - this.borderOffset}px`,
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`,
    );
  }
}
customElements.define("header-drawer", HeaderDrawer);

/**
 * @class ModalOpener
 * @extends HTMLElement
 * @classdesc 自定义元素 ModalOpener，用于打开指定的模态对话框。
 */
class ModalOpener extends HTMLElement {
  constructor() {
    super();

    if (this.hasAttribute("aria-disabled")) return;
    this.modal = document.getElementById(this.getAttribute("aria-controls"));
    if (!this.modal) return;

    // 如果元素没有设置 tabindex（有回退链接的情况）
    if (!this.hasAttribute("tabindex")) {
      this.setAttribute("tabindex", "0");

      const link = this.querySelector("a.fallback-link"); // 只允许最多有一个回退链接
      if (link) link.setAttribute("tabindex", "-1"); // 避免抢占焦点
    }

    this.boundOnOpenerClick = this.onOpenerClick.bind(this);
    this.boundOnKeyDown = this.onKeyDown.bind(this);
    this.addEventListener("click", this.boundOnOpenerClick);
    this.addEventListener("keydown", this.boundOnKeyDown);
  }

  disconnectedCallback() {
    if (this.boundOnOpenerClick)
      this.removeEventListener("click", this.boundOnOpenerClick);
    if (this.boundOnKeyDown)
      this.removeEventListener("keydown", this.boundOnKeyDown);
  }

  onOpenerClick(event) {
    event.preventDefault(); // 当弹窗不存在或者不支持js的情况下，a 标签可作为回退方案，a 标签放在组件内部

    if (this.getAttribute("aria-expanded") === "true") return;
    this.modal.show(this);
  }

  /**
   * 键盘事件
   */
  onKeyDown(event) {
    if (["Space", "Enter"].includes(event.code)) {
      event.preventDefault(); // 防止页面滚动
      this.modal.show(this);
    }
  }
}
customElements.define("modal-opener", ModalOpener);

/**
 * @class ModalDialog
 * @extends HTMLElement
 * @classdesc 自定义元素 ModalDialog，用于显示和隐藏模态对话框。
 */
class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.hasOpened = false; // 打开状态

    // 打开模态时候允许页面滚动
    this.enableScrollWhenOpen = this.hasAttribute("data-enable-scroll");

    // 禁止其它方式关闭
    if (!this.hasAttribute("data-force")) {
      this.addEventListener("keyup", (event) => {
        event.stopPropagation();
        if (event.code && event.code.toUpperCase() === "ESCAPE") this.hide();
      });

      this.addEventListener("click", (event) => {
        event.stopPropagation();

        // this.contains(event.target) 防止嵌套使用
        if (
          this.contains(event.target) &&
          event.target.classList.contains("modal-overlay")
        )
          this.hide();
      });
    }

    // 模板编辑模式
    if (window.Shopify.designMode) {
      document.addEventListener("shopify:section:select", (event) => {
        if (event.detail.sectionId === this.dataset.section) {
          this.show();
        } else {
          this.hide();
        }
      });

      document.addEventListener("shopify:section:deselect", (event) => {
        if (event.detail.sectionId === this.dataset.section) {
          this.hide();
        }
      });
    }
  }

  show(opener) {
    this.openedBy = opener;

    this.setAttribute("open", "");
    if (this.openedBy) this.openedBy.setAttribute("aria-expanded", "true");
    webvista.trapFocus(this);
    this.hasOpened = true;

    if (!this.enableScrollWhenOpen) webvista.disablePageScroll();
  }

  hide() {
    this.removeAttribute("open");

    if (this.openedBy) {
      this.openedBy.setAttribute("aria-expanded", "false");
      webvista.removeTrapFocus(this.openedBy);
    }
    this.hasOpened = false;

    webvista.pauseAllMedia(this); // 关闭所有播放的媒体

    if (!this.enableScrollWhenOpen) webvista.enablePageScroll();
  }
}
customElements.define("modal-dialog", ModalDialog);

/**
 * 抽屉类
 */
class Drawer extends ModalDialog {
  constructor() {
    super();
  }
}
customElements.define("drawer-component", Drawer);

/**
 * @class ConfirmOpener
 * @extends ModalOpener
 * @classdesc 自定义元素 ConfirmOpener，继承自 ModalOpener，用于打开确认对话框。
 */
class ConfirmOpener extends ModalOpener {
  /**
   * @method onOpenerClick
   * @description 处理按钮点击事件，设置确认消息并显示模态对话框。
   */
  onOpenerClick(event) {
    const confirmMessageElement = this.modal.querySelector(".confirm-message");
    const message = this.getAttribute("data-message") || "Are you sure?";
    if (message) confirmMessageElement.innerText = message;

    super.onOpenerClick(event);
  }

  /**
   * @method sendRequest
   * @description 发送 POST 请求到指定的 URL。
   */
  sendRequest() {
    const url = this.getAttribute("data-url");
    if (!url) return;
    const method = this.getAttribute("data-method") || "post";

    Shopify.postLink(url, {
      parameters: { _method: method },
    });
  }
}
customElements.define("confirm-opener", ConfirmOpener);

/**
 * @class ConfirmDialog
 * @extends ModalDialog
 * @classdesc 自定义元素 ConfirmDialog，继承自 ModalDialog，用于显示确认对话框。
 */
class ConfirmDialog extends ModalDialog {
  constructor() {
    super();

    const confirmButton = this.querySelector(".confirm-button");
    if (confirmButton)
      confirmButton.addEventListener("click", this.handleConfirm.bind(this));

    const cancelButton = this.querySelector(".cancel-button");
    if (cancelButton)
      cancelButton.addEventListener("click", this.hide.bind(this));
  }

  /**
   * @method handleConfirm
   * @description 处理确认按钮点击事件，调用触发元素的 sendRequest 方法。
   */
  handleConfirm() {
    if (!this.openedBy) return;

    this.openedBy.sendRequest();
  }
}
customElements.define("confirm-dialog", ConfirmDialog);

class TabPanel extends HTMLElement {
  constructor() {
    super();

    this.tabs = Array.from(this.querySelectorAll(".tab"));
    this.panels = Array.from(this.querySelectorAll(".panel"));
    if (!this.tabs || this.tabs.length < 2) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", this.onTabClick.bind(this));

      tab.setAttribute("tabindex", 0);
      tab.addEventListener("keydown", this.onKeydown.bind(this));
    });
  }

  /**
   * 处理鼠标点击切换 Tab
   * @param event
   */
  onTabClick(event) {
    this.tabChoose(event.currentTarget);
  }

  /**
   * 处理键盘切换 Tab
   * @param event
   */
  onKeydown(event) {
    const key = event.code ? event.code.toUpperCase() : "";

    if (key === "ENTER" || key === "SPACE") {
      this.tabChoose(event.currentTarget);
      event.preventDefault(); // 防止 Space 键的默认滚动行为
    }
  }

  /**
   * 切换 Tab
   * @param tab DomElement
   * @constructor
   */
  tabChoose(tab) {
    const panel = document.getElementById(tab.getAttribute("aria-controls"));
    if (!tab || !panel) return;

    const currentTab = this.querySelector(".tab[aria-selected=true]");
    const currentPanel = this.querySelector(".panel:not([aria-hidden])");

    if (tab !== currentTab) {
      currentTab.setAttribute("aria-selected", "false");
      currentPanel.setAttribute("aria-hidden", "true");

      tab.setAttribute("aria-selected", true);
      panel.removeAttribute("aria-hidden");
    }

    this.scrollableContentViewer =
      this.scrollableContentViewer || tab.closest("scrollable-content-viewer");
    if (this.scrollableContentViewer)
      this.scrollableContentViewer.slideContentByItem(tab);
  }
}
customElements.define("tab-panel", TabPanel);

/**
 * DetailsDisclosure 类
 * 这个类用于创建一个自定义的 HTML 元素，特别适用于管理 <details> 元素的展开和收起行为。
 * 它主要用于控制与 <details> 和 <summary> 元素相关的动画，并处理用户与这些元素的交互。
 * 当 <details> 元素的状态改变（展开或收起）时，它会相应地播放或取消内容区域的动画。
 * 这个类还提供了一个方法来手动关闭 <details> 元素，确保可访问性和一致的状态管理。
 */
class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle.querySelector("summary").nextElementSibling;
    if (!this.mainDetailsToggle || !this.content) return;

    this.observeVisibility();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  /**
   * 监听元素可见性
   * 可见的时候执行一操作
   */
  observeVisibility() {
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 如果默认是展开的，初始化高度
            this.handleWhenVisibility();
            observer.unobserve(this);
          }
        });
      },
      {
        root: null,
        threshold: 0.1,
      },
    );

    this.observer.observe(this);
  }

  /**
   * 当元素可见的时候执行
   */
  handleWhenVisibility() {
    if (this.mainDetailsToggle.hasAttribute("open")) this.onToggle();

    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onToggle() {
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute(
        "aria-expanded",
        this.mainDetailsToggle.hasAttribute("open"),
      );

    // 异步添加防止动画未应用
    setTimeout(() => {
      this.mainDetailsToggle.classList.toggle(
        "has-opened",
        this.mainDetailsToggle.hasAttribute("open"),
      );
    });
  }

  open() {
    this.mainDetailsToggle.setAttribute("open", "true");
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
  }
}
customElements.define("details-disclosure", DetailsDisclosure);

/**
 * DropMenu 类
 * 这个类继承自 DetailsDisclosure，提供了特定于下拉菜单和选择器的功能。
 * 除了继承自基类的展开和收起行为的管理外，它还添加了焦点管理的特性。
 * 当用户将焦点从该元素移出时，DropMenu 类会自动关闭 <details> 元素。
 * 支持鼠标悬停打开
 * 还可以根据指定容器的大小自动调整宽度，防止超出或者被隐藏。
 * 适用于页眉【下拉菜单】、【地区和语言选择器】等需要焦点控制和动画支持的交互元素。
 */
class DropMenu extends DetailsDisclosure {
  constructor() {
    super();

    this.hoverCloseTimer = null;
  }

  handleWhenVisibility() {
    super.handleWhenVisibility();
    this.mainDetailsToggle.addEventListener(
      "focusout",
      this.onFocusOut.bind(this),
    );

    // 鼠标悬停打开
    if (this.hasAttribute("data-hover-open")) {
      this.mainDetailsToggle.addEventListener("mouseenter", () => {
        if (this.hoverCloseTimer) {
          clearTimeout(this.hoverCloseTimer);
          this.hoverCloseTimer = null;
        }
        this.open();
      });
      this.mainDetailsToggle.addEventListener("mouseleave", () => {
        if (this.hoverCloseTimer) clearTimeout(this.hoverCloseTimer);
        this.hoverCloseTimer = setTimeout(this.close.bind(this), 300);
      });
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    // 调整位置，防止超出屏幕区域
    if (this.mainDetailsToggle.hasAttribute("data-constrain"))
      this.adjustContentPosition();

    // 切换 header 激活状态
    const header = this.closest(".section-header");
    if (header)
      header.classList.toggle(
        "header--is-active",
        this.mainDetailsToggle.hasAttribute("open"),
      );

    super.onToggle();
  }

  /**
   * 调整内容位置，使其不能超过【右侧】限制区域
   * 默认是视口屏幕区域
   * data-area 可设置自定义边界区域
   * data-adjust 自动调整位置
   */
  adjustContentPosition() {
    const needAdjust = this.mainDetailsToggle.hasAttribute("data-adjust"); // 是否需要自动调整位置

    if (!this.mainDetailsToggle.hasAttribute("open")) {
      this.mainDetailsToggle.classList.remove("position--exceed");

      // 自动调整位置，重置位置
      if (needAdjust) {
        this.content.style.left = null;
      }
    } else {
      // 调整位置，使其不能超过限制区域
      let areaRightSidePosition = document.documentElement.clientWidth; // 右边界，默认屏幕右侧，不包含滚动条
      let areaLeftSidePosition = 0; // 左边界，默认屏幕左侧
      if (this.dataset.area) {
        // 如果设置了自定义边界
        const areaRect = document
          .querySelector(this.dataset.area)
          ?.getBoundingClientRect();
        if (areaRect) {
          areaRightSidePosition = areaRect.right;
          areaLeftSidePosition = areaRect.left;
        }
      }

      const contentRect = this.content.getBoundingClientRect();
      // 偏移量
      let offset = contentRect.right - areaRightSidePosition;

      if (offset > 0) {
        // 超出区域
        this.mainDetailsToggle.classList.add("position--exceed");

        if (needAdjust) {
          this.content.style.left = `${-Math.ceil(offset)}px`;
        }
      }
    }

    this.mainDetailsToggle.classList.toggle(
      "position--constrained",
      this.mainDetailsToggle.hasAttribute("open"),
    );
  }
}
customElements.define("drop-menu", DropMenu);

/**
 * 延迟播放媒体
 */
class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    this.template = this.querySelector("template");
    if (!this.template) return;

    // 点击按钮加载
    const playButton = this.querySelector(".media-play-button");
    if (playButton) {
      playButton.removeAttribute("disabled");
      playButton.addEventListener("click", this.loadContent.bind(this));
    }

    // 静音按钮
    this.muteButton = this.querySelector(".mute-button");
    if (this.muteButton)
      this.muteButton.addEventListener("click", this.toggleMute.bind(this));

    // data-load-when-visible: 当内容可见的时候加载视频资源
    if (this.hasAttribute("data-load-when-visible")) {
      this.initObserver();
    }
  }

  /**
   * 监听资源滚动到视口区域
   * 自动播放视频
   */
  initObserver() {
    const options = {
      root: null,
      rootMargin: "100px 0px 100px 0px",
    };

    new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.loadContent();
          observer.disconnect();
        }
      });
    }, options).observe(this);
  }

  /**
   * 获取媒体资源
   * @param focus
   */
  loadContent(focus = true) {
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.template.content.firstElementChild.cloneNode(true),
      );
      this.deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe"),
      );

      // 发送加载完成事件
      this.dispatchEvent(
        new CustomEvent("deferred-loaded", {
          bubbles: true,
        }),
      );

      // 下次渲染时候设置加载状态，防止火狐浏览器自动聚焦导致的页面闪中
      requestAnimationFrame(() => {
        this.setAttribute("loaded", "true");
      });

      if (this.deferredElement.nodeName === "VIDEO") {
        // 有静音按钮，监听音量变化
        if (this.muteButton) {
          this.deferredElement.addEventListener(
            "volumechange",
            this.onVolumeChange.bind(this),
          );

          // 初始化静音按钮状态
          this.muteButton.setAttribute(
            "aria-pressed",
            String(this.deferredElement.hasAttribute("muted")),
          );
        }

        // 自动播放
        if (this.deferredElement.getAttribute("autoplay")) {
          this.deferredElement.play(); // 播放视频
        }

        // 播放完成
        this.deferredElement.addEventListener(
          "ended",
          this.removeContent.bind(this),
        );
      }

      if (focus) this.deferredElement.focus();
    }
  }

  /**
   * 播放完成后移除资源
   */
  removeContent() {
    if (this.deferredElement) {
      this.deferredElement.remove();
      this.removeAttribute("loaded");
    }
  }

  /**
   * 处理音量变化
   */
  onVolumeChange() {
    this.muteButton.setAttribute(
      "aria-pressed",
      String(this.deferredElement.muted),
    );
  }

  /**
   * 切换静音状态
   */
  toggleMute() {
    if (this.deferredElement?.nodeName !== "VIDEO") return;

    this.deferredElement.muted = !this.deferredElement.muted;

    // 发送自定义事件
    this.dispatchEvent(
      new CustomEvent("video-mute", {
        bubbles: true,
        detail: {
          muted: this.deferredElement.muted,
        },
      }),
    );
  }
}
customElements.define("deferred-media", DeferredMedia);

/**
 * 超多变体的变体属性选择器
 * 支持超过250个变体数量
 */
class HighVariantSelects extends HTMLElement {
  constructor() {
    super();

    this.productForm = document.getElementById(
      `product-form-${this.dataset.section}`,
    );

    this.addEventListener("change", this.onVariantChange.bind(this));
  }

  /**
   * 变体变化处理
   */
  onVariantChange(event) {
    this.toggleAddButtonStatus(true, "");
    this.removeErrorMessage(); // 移除product-form的错误消息

    this.updateOptions(event.target);
    this.renderProductInfo(event.target);
  }

  /**
   * 获取选择器每个属性的值id数组
   * 返回值的集合数组
   */
  updateOptions(target) {
    if (target.tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");
    }

    this.optionValueIds = Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked"),
    ).map((element) => element.dataset.optionValueId);
  }

  /**
   * 更新媒体当前展示
   */
  updateMedia() {
    if (!this.currentVariant?.featured_media?.id) return;

    // 设置当前展示的媒体
    const mediaGallery = document.getElementById(
      `Media-Gallery-${this.dataset.section}`,
    );
    if (mediaGallery)
      mediaGallery.updateGallery(this.currentVariant.featured_media.id);
  }

  // 更新浏览器地址
  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === "false") return;
    window.history.replaceState(
      {},
      "",
      `${this.dataset.url}?variant=${this.currentVariant.id}`,
    );
  }

  // 更新表单的variantId
  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
    );

    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      // 手动触发Change事件
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  /**
   * 设置变体不可售状态
   */
  setUnavailable() {
    // 隐藏指定元素
    const hidden_element_ids = [
      `Price-${this.dataset.section}`,
      `Inventory-${this.dataset.section}`,
      `Sku-${this.dataset.section}`,
      `Price-Per-Item-${this.dataset.section}`,
      `Volume-Note-${this.dataset.section}`,
      `Volume-${this.dataset.section}`,
      `Quantity-Rules-${this.dataset.section}`,
    ];

    hidden_element_ids.forEach((id) => {
      const element = document.getElementById(id);
      if (element) element.classList.add("hidden");
    });
  }

  // 更新本地取货、配送
  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector("pickup-availability");
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant["available"]) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute("available");
      pickUpAvailability.innerHTML = "";
    }
  }

  /**
   * 移除product-form的错误消息
   */
  removeErrorMessage() {
    if (!this.productForm) return;

    this.productForm.closest("product-form")?.handleErrorMessage();
  }

  /**
   * 更新产品信息
   * 价格、库存、SKU、更新数量选择器和购买按钮区域（包括添加购物车，动态结账，到货通知按钮），分享，赠品，迷你结账，到货通知表单
   * 发送变体变化广播
   * @param target 目标焦点元素
   */
  renderProductInfo(target) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    webvista
      .fetchHtml(
        `${this.dataset.url}?option_values=${this.optionValueIds.join(",")}&section_id=${this.sourceSectionId}`,
        this.abortController.signal,
      )
      .then((html) => {
        this.updateBlock(html, "Variant-Selects"); // 更新选择器
        this.getCurrentVariant(); // 获取当前变体

        if (!this.currentVariant) {
          // 变体不存在
          this.toggleAddButtonStatus(
            true,
            window["variantStrings"]["unavailable"],
          );
          return this.setUnavailable();
        }

        this.updateVariantInput();
        this.updateURL();
        this.updatePickupAvailability();

        // 更新当前变体媒体
        this.updateMedia();

        // 同步添加购物车按钮
        const addCartButtonSource = html.getElementById(
          `Product-Submit-Button-${this.sourceSectionId}`,
        );
        this.toggleAddButtonStatus(
          addCartButtonSource
            ? addCartButtonSource.hasAttribute("disabled")
            : true,
          addCartButtonSource
            ? addCartButtonSource.querySelector(".button-text").innerText
            : window["variantStrings"]["soldOut"],
        );

        this.updateBlock(html, "Price");
        this.updateBlock(
          html,
          "Inventory",
          true,
          null,
          ({ innerHTML }) => innerHTML === "",
        );
        this.updateBlock(
          html,
          "Sku",
          true,
          null,
          ({ innerHTML }) => innerHTML === "",
        );

        this.updateBlock(html, "Share-Link"); // 分享链接
        this.updateBlock(html, "Product-Gift"); // 赠品Gift
        this.updateBlock(html, "Product-Mini-Checkout", true, [
          ".product-image",
          ".product-content",
        ]); // 迷你结账
        this.updateBlock(
          html,
          "Notify-Email-Button",
          false,
          null,
          ({ classList }) => classList.contains("hidden"),
        ); // 同步到货订阅按钮
        this.updateBlock(html, "Notify-Email"); // 更新到货通知表单

        // 重新初始化一些监听
        webvista.initLazyImages();
        webvista.initTooltips();

        // 发送广播
        webvista.publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.dataset.section, // 目标 section id
            html, // 源 html
            variant: this.currentVariant,
          },
        });
      })
      .then(() => {
        document.querySelector(`#${target.id}`)?.focus();
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.log("Fetch aborted by user");
        } else {
          console.error(error);
        }
      });
  }

  /**
   * 更新页面区块内容
   * @param {Document} html - 包含新内容的HTML文档
   * @param {string} blockId - 区块基础ID
   * @param {boolean} [replaceEntireBlock=true] - 是否替换整个区块内容
   * @param {string[]} [selectorsToReplace] - 需要单独替换的选择器数组
   * @param {Function} [shouldHideCallback] - 决定区块是否隐藏的回调函数
   */
  updateBlock(
    html,
    blockId,
    replaceEntireBlock = true,
    selectorsToReplace,
    shouldHideCallback = (sourceElement) => false,
  ) {
    // 获取源元素和目标元素
    const sourceElement = html.getElementById(
      `${blockId}-${this.sourceSectionId}`,
    );
    const targetElement = document.getElementById(
      `${blockId}-${this.dataset.section}`,
    );

    // 如果元素不存在则直接返回
    if (!sourceElement || !targetElement) return;

    // 如果源Id存在，需要替换内容里面的源id为目标id
    if (this.dataset.originalSection) {
      // 创建源元素的克隆，避免修改原始DOM
      const clonedSource = sourceElement.cloneNode(true);
      // 将sourceElement中的this.dataset.originalSection字符串替换为this.dataset.section字符串
      const htmlContent = clonedSource.innerHTML;
      const regex = new RegExp(this.dataset.originalSection, "g");
      clonedSource.innerHTML = htmlContent.replace(regex, this.dataset.section);
      // 使用处理后的克隆元素作为新的源元素
      sourceElement.innerHTML = clonedSource.innerHTML;
    }

    // 替换内容逻辑
    if (replaceEntireBlock) {
      if (!selectorsToReplace?.length) {
        // 替换整个区块内容
        targetElement.innerHTML = sourceElement.innerHTML;
      } else {
        // 替换指定选择器的内容
        selectorsToReplace.forEach((selector) => {
          const targetSubElement = targetElement.querySelector(selector);
          const sourceSubElement = sourceElement.querySelector(selector);

          if (targetSubElement && sourceSubElement) {
            targetSubElement.replaceWith(sourceSubElement.cloneNode(true));
          }
        });
      }
    }

    // 更新区块可见性
    targetElement.classList.toggle("hidden", shouldHideCallback(sourceElement));
  }

  /**
   * 添加购物车按钮状态
   * @param disable
   * @param text
   */
  toggleAddButtonStatus(disable = true, text) {
    if (!this.productForm) return;

    const addCartButtons = this.productForm.querySelectorAll(
      'button[type="submit"]',
    );
    const dynamicCheckout = this.productForm.querySelector(".dynamic-checkout"); // 动态结账

    if (disable) {
      addCartButtons.forEach((addButton) => {
        addButton.setAttribute("disabled", "disabled");
        if (!text) return;

        const addButtonTextElement = addButton.querySelector(".button-text");
        if (addButtonTextElement) addButtonTextElement.textContent = text;
      });

      // 隐藏动态支付
      if (dynamicCheckout && text) dynamicCheckout.classList.add("hidden");
    } else {
      addCartButtons.forEach((button) => {
        button.removeAttribute("disabled");

        const addButtonTextElement = button.querySelector(".button-text");
        if (!addButtonTextElement) return;

        addButtonTextElement.textContent = text
          ? text
          : window["variantStrings"]["addToCart"];
      });

      // 显示动态结账
      if (dynamicCheckout) dynamicCheckout.classList.remove("hidden");
    }
  }

  /**
   * 获取当前变体json对象
   */
  getCurrentVariant() {
    const json = this.querySelector(
      "script[data-selected-variant]",
    ).textContent;
    this.currentVariant = !!json ? JSON.parse(json) : null;
  }

  /**
   * 获取源 section id
   * @returns {string}
   */
  get sourceSectionId() {
    return this.dataset.originalSection || this.dataset.section;
  }
}
customElements.define("high-variant-selects", HighVariantSelects);

/**
 * Product recommendations (related / complementary).
 * Lazy-loads via IntersectionObserver when the element approaches the viewport.
 */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);

        this.fetchData();
      },
      { rootMargin: "0px 0px 400px 0px" },
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  fetchData() {
    webvista
      .fetchHtml(this.dataset.url)
      .then((html) => {
        this.renderHtml(html);
      })
      .catch(() => {
        this.remove();
      });
  }

  renderHtml(html) {
    const sourceRecommendationMain = html.querySelector(
      `#${this.id} .recommendation-main`,
    );

    if (sourceRecommendationMain) {
      this.querySelector(".products-container").innerHTML =
        sourceRecommendationMain.outerHTML;

      // Re-initialize lazy images and tooltips after DOM update
      webvista.initLazyImages();
      webvista.initTooltips();
    } else {
      this.remove();
    }
  }
}
customElements.define("product-recommendations", ProductRecommendations);

/**
 * 产品卡片处理类
 * 主要是为了处理Swatch图片切换
 */
class ProductCard extends HTMLElement {
  constructor() {
    super();

    this.colorSwatches = this.querySelector(".color-swatches");
    if (this.colorSwatches) this.initSwatchHandle();
  }

  /**
   * 初始化颜色选择器
   */
  initSwatchHandle() {
    this.colorSwatches.querySelectorAll(".color-swatch").forEach((swatch) => {
      swatch.addEventListener("click", this.onSwatchClick.bind(this));
    });
  }

  onSwatchClick(event) {
    event.stopPropagation();

    const swatch = event.currentTarget;
    const currentSwatch = this.colorSwatches.querySelector(
      ".color-swatch.active",
    );
    const index = swatch.getAttribute("data-index");

    const variantImage = this.querySelector(
      `.product-card-variant-image[data-index="${index}"]`,
    );
    if (!variantImage) return;

    if (currentSwatch) this.inActiveSwatch(currentSwatch);
    if (swatch !== currentSwatch) this.activeSwatch(swatch, variantImage);
  }

  /**
   * 激活 swatch
   * @param swatch
   * @param image
   */
  activeSwatch(swatch, image) {
    swatch.classList.add("active");
    this.classList.add("has-swatch-active");
    image.classList.remove("hidden");
  }

  /**
   * 取消激活 swatch
   * @param swatch
   */
  inActiveSwatch(swatch) {
    swatch.classList.remove("active");
    this.classList.remove("has-swatch-active");
    this.querySelector(
      `.product-card-variant-image[data-index="${swatch.getAttribute("data-index")}"]`,
    )?.classList.add("hidden");
  }
}
customElements.define("product-card", ProductCard);

/**
 * 粘性滚动效果
 * data-sticky-distance 粘性距离，值越大，粘性时间越久
 * data-top-halt 滚动到顶部的停顿距离，值越大停顿越久
 * data-bottom-halt 滚动到底部的停顿距离，值越大停顿越久
 * data-trigger-position 触发效果的位置，top|center|bottom; 默认值 top，滚动到页面顶部时候触发
 * data-mobile-disabled 手机端禁用
 */
class StickyScroll extends HTMLElement {
  constructor() {
    super();

    // 手机端禁用
    if (this.hasAttribute("data-mobile-disabled") && webvista.isMobileScreen())
      return;

    this.hasListenWindowScroll = false; // 是否已经监听页面滚动
    this.inView = false; // 是否在视区内

    this.preStatus = 0; // 前状态 0: 未开始；1: 效果中；2: 已结束
    this.currentStatus = 0; // 当前状态

    this.stickyContainer = this.querySelector(".sticky-scroll-container");
    if (!this.stickyContainer) return;

    this.changeRatio = 0;
    this.stickyScrollDistance =
      parseInt(this.getAttribute("data-sticky-distance")) || 0; // 粘性效果距离，距离越大，动画时间越长
    this.getTriggerOffset(); // 获取触发位置，可以实现提前触发或者延迟触发
    this.topHalt = parseInt(this.getAttribute("data-top-halt")) || 0; // 上停顿距离
    this.bottomHalt = parseInt(this.getAttribute("data-bottom-halt")) || 0; // 下停顿距离

    if (
      this.stickyScrollDistance > 0 &&
      this.stickyScrollDistance - this.topHalt - this.bottomHalt <= 0
    )
      return;

    this.containerHeight = this.offsetHeight; // 容器高度
    this._rafId = null; // rAF 节流 ID
    this.boundHandleScrollEffect = this._onScroll.bind(this); // 绑定滚动效果
    if (!this.hasAttribute("data-initialized")) this.initializeHeight();

    this.observeScrollIntoView();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this.removeListenWindowScroll();
  }

  /**
   * scroll 事件回调，使用 rAF 节流
   */
  _onScroll() {
    if (this._rafId) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      this.handleScrollEffect();
    });
  }

  observeScrollIntoView() {
    this.observer = new IntersectionObserver(
      (entries) => {
        this.inView = entries[0].isIntersecting;
        if (
          this.inView &&
          this.hasAttribute("data-initialized") &&
          this.referencePageScrollY == null
        )
          this.getReferenceScrollY();

        this.inView
          ? this.listenWindowScroll()
          : this.removeListenWindowScroll();
      },
      {
        root: null,
        rootMargin: "0px 0px -200px 0px",
        threshold: 0,
      },
    );

    this.observer.observe(this);
  }

  /**
   * 获取相对于页面顶部的触发位置
   * 可以实现提前触发或者延迟触发
   */
  getTriggerOffset() {
    this.triggerPosition = this.getAttribute("data-trigger-position") || "top";
    const clientHeight = document.documentElement.clientHeight;

    if (this.triggerPosition === "top") {
      this.triggerOffset = 0;
    } else if (this.triggerPosition === "center") {
      this.triggerOffset = clientHeight / 2;
    } else {
      this.triggerOffset = clientHeight;
    }
  }

  /**
   * 获取参照的 scrollY
   * 之后会获取相对的滚动距离
   */
  getReferenceScrollY() {
    const rect = this.getBoundingClientRect();
    const pageScrollTop = window.scrollY || document.documentElement.scrollTop;
    this.referencePageScrollY = pageScrollTop + rect.top - this.triggerOffset;
  }

  /**
   * 监听页面滚动
   */
  listenWindowScroll() {
    if (!this.hasListenWindowScroll) {
      window.addEventListener("scroll", this.boundHandleScrollEffect);
      this.hasListenWindowScroll = true;
    }
  }

  /**
   * 取消页面滚动监听
   */
  removeListenWindowScroll() {
    if (this.hasListenWindowScroll) {
      window.removeEventListener("scroll", this.boundHandleScrollEffect);
      this.hasListenWindowScroll = false;
    }
  }

  /**
   * 设置容器高度
   */
  initializeHeight() {
    this.style.height = `${this.containerHeight + this.stickyScrollDistance}px`;
    this.setAttribute("data-initialized", "true");
  }

  /**
   * 处理滚动效果
   * 有前后停顿效果
   */
  handleScrollEffect() {
    const currentPageScrollY =
      window.scrollY || document.documentElement.scrollTop;
    const rect = this.getBoundingClientRect();
    const clientHeight = document.documentElement.clientHeight;

    let newRatio;
    if (rect.top <= this.triggerOffset && rect.bottom > clientHeight) {
      // 触发效果的滚动范围
      const relativeScrollY = currentPageScrollY - this.referencePageScrollY;
      const ratio =
        (relativeScrollY - this.topHalt) /
        (this.stickyScrollDistance +
          this.triggerOffset -
          this.topHalt -
          this.bottomHalt);
      newRatio = Math.min(1, Math.max(0, ratio));
    } else if (rect.top > this.triggerOffset) {
      newRatio = 0;
    } else {
      newRatio = 1;
    }

    // 值未变化则跳过后续写入
    if (newRatio === this.changeRatio) return;
    this.changeRatio = newRatio;

    // 当前状态
    if (this.changeRatio <= 0) {
      this.currentStatus = 0; // 未开始
    } else if (this.changeRatio >= 1) {
      this.currentStatus = 2; // 已结束
    } else {
      this.currentStatus = 1; // 效果中
    }

    // 状态变化时更新 class
    if (this.preStatus !== this.currentStatus) {
      if (this.currentStatus === 1) {
        this.classList.add("sticky-scroll--effect");
        this.classList.remove("sticky-scroll--end");
      } else if (this.currentStatus === 2) {
        this.classList.add("sticky-scroll--end");
        this.classList.remove("sticky-scroll--effect");
      } else {
        this.classList.remove("sticky-scroll--effect");
        this.classList.remove("sticky-scroll--end");
      }
    }

    this.preStatus = this.currentStatus; // 更新状态
    this.style.setProperty("--change-ratio", this.changeRatio);
  }
}
customElements.define("sticky-scroll", StickyScroll);

/**
 * 数字递增效果
 */
class IncrementNumber extends HTMLElement {
  constructor() {
    super();
    // 初始化起始数字
    this.start = 0;
    // 当前显示的数字
    this.currentNumber = 0;

    // 使用 IntersectionObserver 观察元素是否进入视窗
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (entries[0].isIntersecting) {
          // 当元素进入视窗时，初始化并开始动画
          this.init();
          // 停止观察
          observer.disconnect();
        }
      },
      {
        root: null, // 使用视窗作为根
        rootMargin: "-300px 0px -300px 0px",
      },
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  // 初始化目标数字和动画持续时间
  init() {
    // 提取数字部分
    const textContent = this.textContent;
    const numberMatch = textContent.match(/\d+/);
    if (!numberMatch) return;

    this.targetNumber = parseInt(numberMatch[0], 10);

    this.remainingText = textContent.replace(numberMatch[0], "[number]"); // 提取非数字部分
    this.duration = parseInt(this.getAttribute("data-duration"), 10) || 2000;
    this.startTime = Date.now();
    this.updateNumber();
  }

  // 缓动函数，定义非线性进度
  easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  // 更新数字的方法
  updateNumber() {
    const currentTime = Date.now();
    const elapsedTime = currentTime - this.startTime;
    const progress = Math.min(elapsedTime / this.duration, 1);
    const easedProgress = this.easeInOutQuad(progress);
    this.currentNumber = Math.floor(
      this.start + easedProgress * (this.targetNumber - this.start),
    );
    this.textContent = this.remainingText.replace(
      "[number]",
      this.currentNumber,
    );
    if (progress < 1) {
      requestAnimationFrame(this.updateNumber.bind(this));
    }
  }
}
customElements.define("increment-number", IncrementNumber);

/**
 * 动态打字效果
 */
class TypingWords extends HTMLElement {
  constructor() {
    super();
    this.index = 0;

    this.observer = new IntersectionObserver((entries, observer) => {
      if (entries[0].isIntersecting) {
        this.text = this.getAttribute("data-text") || "";
        this.interval = this.getAttribute("data-interval")
          ? parseInt(this.getAttribute("data-interval"))
          : 10;

        this.initTyping();

        observer.disconnect();
      }
    });

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    this.clearTimers();
  }

  clearTimers() {
    if (this.timer) clearInterval(this.timer);
    if (this.restartTimer) clearTimeout(this.restartTimer);
  }

  initTyping() {
    this.textContent = ""; // 清空内容
    this.index = 0; // 重置索引
    this.timer = setInterval(() => this.type(), 150);
  }

  type() {
    if (this.index < this.text.length) {
      this.textContent += this.text.charAt(this.index);
      this.index++;
    } else {
      clearInterval(this.timer);
      this.restartTimer = setTimeout(
        () => this.initTyping(),
        this.interval * 1000,
      );
    }
  }
}
customElements.define("typing-words", TypingWords);

class CustomCopyText extends HTMLElement {
  constructor() {
    super();

    const debounceHandle = webvista.debounce(() => {
      if (typeof Shopify !== "undefined" && Shopify.designMode) {
        return webvista.popToast(
          window["copyStrings"]["copyDisabled"],
          "warning",
        );
      }

      navigator.clipboard
        .writeText(this.getAttribute("data-text"))
        .then(() => {
          webvista.popToast(this.getAttribute("data-message"), "success");
        })
        .catch((err) => {
          webvista.popToast(window["copyStrings"]["copyFailed"], "error");
        });
    }, 500);

    this.addEventListener("click", debounceHandle);

    // 监听键盘事件，支持空格和回车
    this.addEventListener("keydown", (event) => {
      const key = event.code ? event.code.toUpperCase() : "";

      if (key === "ENTER" || key === "SPACE") {
        event.preventDefault(); // 防止页面滚动（空格键的默认行为）
        debounceHandle(); // 执行复制操作
      }
    });
  }
}
customElements.define("custom-copy-text", CustomCopyText);

class LinkForm extends HTMLElement {
  constructor() {
    super();

    // 使用事件委托来处理点击事件，提高性能
    this.addEventListener("click", this.onItemClick.bind(this));
  }

  /**
   * @method onItemClick
   * @description 链接的点击事件处理函数。阻止默认行为，设置输入值，并提交表单。
   * @param {Event} event - 点击事件对象
   */
  onItemClick(event) {
    if (event.target.tagName !== "A") return;

    event.preventDefault();
    const form = this.querySelector("form");
    if (!form) return;

    const input = this.querySelector("input.link-value");
    if (input) input.value = event.target.dataset.value;

    form.submit();
  }
}
customElements.define("link-form", LinkForm);

/**
 * 文本容器，有限制高度功能
 */
class ExpandableContainer extends HTMLElement {
  constructor() {
    super();

    if (!this.hasAttribute("data-limit-height")) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.init();
          this.observer.disconnect();
        }
      },
      {
        rootMargin: "-300px 0px -300px 0px",
      },
    );
    this.observer.observe(this);
  }

  init() {
    if (this.contentMain.scrollHeight <= this.contentMain.clientHeight) return;

    this.classList.add("has-exceed");
    this.expandButton?.addEventListener("click", this.toggleExpand.bind(this));
    this.expandButton?.addEventListener("keydown", this.onKeyDown.bind(this));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  get contentMain() {
    return this.querySelector(".content-main");
  }

  get expandButton() {
    return this.querySelector(".expand-button");
  }

  get readMoreText() {
    return this.querySelector(".read-more-text");
  }

  get readLessText() {
    return this.querySelector(".read-less-text");
  }

  toggleExpand() {
    const isExpanded = this.classList.toggle("has-expand");
    this.expandButton.setAttribute("aria-expanded", isExpanded.toString());

    // 为屏幕阅读器提供反馈
    if (isExpanded) {
      this.readMoreText.classList.add("hidden");
      this.readLessText.classList.remove("hidden");
    } else {
      this.readMoreText.classList.remove("hidden");
      this.readLessText.classList.add("hidden");
    }
  }

  onKeyDown(event) {
    // Handle Enter or Space key (standard for button activation)
    if (["Space", "Enter"].includes(event.code)) {
      event.preventDefault(); // Prevent scroll when Space is pressed
      this.toggleExpand();
    }
  }
}
customElements.define("expandable-container", ExpandableContainer);


// Renkleri mor yapma

(function() {
    // Aranacak renklerin RGB ve HEX karşılıkları (Dizi olarak tanımlandı)
    const targetColorsRGB = ["rgb(210, 39, 49)", "rgb(181, 0, 181)"]; // Sırasıyla: #d22731 ve #b500b5
    const targetColorsHEX = ["#d22731", "#b500b5"];
    
    // Dönüştürülecek yeni renk
    const newColor = "#5d0be4";

    // Sözde elementler (::after, ::before) için dinamik stil dosyası oluştur
    const styleTag = document.createElement('style');
    document.head.appendChild(styleTag);
    let customRuleCount = 0;

    function replaceColors(element) {
        if (element.nodeType !== 1) return;

        const style = window.getComputedStyle(element);

        // Stil değiştirme yardımcısı (Artık birden fazla rengi kontrol ediyor)
        const checkAndReplace = (computedValue, cssProp) => {
            if (!computedValue) return;
            
            let updatedValue = computedValue;
            let isModified = false;

            targetColorsRGB.forEach(targetRGB => {
                if (updatedValue.includes(targetRGB)) {
                    updatedValue = updatedValue.split(targetRGB).join(newColor);
                    isModified = true;
                }
            });

            if (isModified) {
                element.style.setProperty(cssProp, updatedValue, 'important');
            }
        };

        // Standart CSS Özellikleri (Gölge ve Çerçeveler dahil)
        checkAndReplace(style.color, 'color');
        checkAndReplace(style.backgroundColor, 'background-color');
        checkAndReplace(style.borderColor, 'border-color');
        checkAndReplace(style.outlineColor, 'outline-color');
        checkAndReplace(style.boxShadow, 'box-shadow');

        // SVG ve İkonlar için kontrol
        targetColorsRGB.forEach((targetRGB, index) => {
            const targetHEX = targetColorsHEX[index];
            const elemFill = element.getAttribute('fill') ? element.getAttribute('fill').toLowerCase() : null;
            const elemStroke = element.getAttribute('stroke') ? element.getAttribute('stroke').toLowerCase() : null;

            if (style.fill === targetRGB || elemFill === targetHEX) {
                element.style.setProperty('fill', newColor, 'important');
                if (element.hasAttribute('fill')) element.setAttribute('fill', newColor);
            }
            if (style.stroke === targetRGB || elemStroke === targetHEX) {
                element.style.setProperty('stroke', newColor, 'important');
                if (element.hasAttribute('stroke')) element.setAttribute('stroke', newColor);
            }
        });

        // JS ile doğrudan ulaşılamayan ::before ve ::after elementlerini kontrol etme
        const checkPseudo = (pseudo) => {
            const pStyle = window.getComputedStyle(element, pseudo);
            let newRules = '';
            
            const processPseudoProp = (propName, propValue) => {
                if (!propValue) return;
                let updatedValue = propValue;
                let isModified = false;
                
                targetColorsRGB.forEach(targetRGB => {
                    if (updatedValue.includes(targetRGB)) {
                        updatedValue = updatedValue.split(targetRGB).join(newColor);
                        isModified = true;
                    }
                });
                
                if (isModified) {
                    newRules += `${propName}: ${updatedValue} !important; `;
                }
            };

            processPseudoProp('border-color', pStyle.borderColor);
            processPseudoProp('box-shadow', pStyle.boxShadow);
            processPseudoProp('background-color', pStyle.backgroundColor);
            processPseudoProp('color', pStyle.color);

            // Eğer gizli bir çerçeve/gölge bulunduysa, o elemente özel class atayıp CSS yaz
            if (newRules !== '') {
                const uniqueClass = `color-override-${customRuleCount++}`;
                element.classList.add(uniqueClass);
                styleTag.sheet.insertRule(`.${uniqueClass}${pseudo} { ${newRules} }`, styleTag.sheet.cssRules.length);
            }
        };

        checkPseudo('::before');
        checkPseudo('::after');
    }

    // İlk tarama
    function scanWholePage() {
        document.querySelectorAll('*').forEach(replaceColors);
    }
    scanWholePage();

    // Aktif dinleme (sayfaya sonradan eklenenler için)
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.addedNodes) {
                mutation.addedNodes.forEach((node) => {
                    replaceColors(node);
                    if (node.querySelectorAll) {
                        node.querySelectorAll('*').forEach(replaceColors);
                    }
                });
            }
        });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    console.log("%cÇoklu Renk Değiştirme Scripti Aktif! Belirtilen renkler anında mora dönüştürülüyor.", "color: #5d0be4; font-weight: bold;");
})();


    // Tahmini toplam yazısını Toplam: olarak değişen kod
(function() {
    // Sadece "total-title" class'ını hedef al
    function metniDegistir() {
        const basliklar = document.querySelectorAll('.footer-subtotal .total-title');
        basliklar.forEach(function(baslik) {
            // Eğer metnin içinde "Tahmini" kelimesi geçiyorsa değiştir
            if (baslik.innerText.includes('Tahmini')) {
                baslik.innerText = 'Toplam:';
            }
        });
    }

    // Sayfa ilk açıldığında çalıştır
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', metniDegistir);
    } else {
        metniDegistir();
    }

    // Sepet her açılıp kapandığında veya güncellendiğinde ZORLA değiştir
    const observer = new MutationObserver(function() {
        metniDegistir();
    });

    observer.observe(document.body, { 
        childList: true, 
        subtree: true,
        characterData: true
    });
})();

    // Fiyat etiketini bulup TRY yazısını TL yapan fonksiyon
(function() {
    function paraBiriminiDegistir() {
        // 'total-price' class'ına sahip tüm elementleri bul
        const fiyatlar = document.querySelectorAll('.total-price');
        
        fiyatlar.forEach(function(fiyat) {
            // Sadece içinde "TRY" yazıyorsa müdahale et (sonsuz döngüyü engeller)
            if (fiyat.innerText.includes('TRY')) {
                // TRY kelimesini TL ile değiştir
                fiyat.innerText = fiyat.innerText.replace('TRY', 'TL');
            }
        });
    }

    // 1. Aşama: Sayfa ilk açıldığında çalıştır
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', paraBiriminiDegistir);
    } else {
        paraBiriminiDegistir();
    }

    // 2. Aşama: Sepet güncellemelerini takip edip anında müdahale et
    const observer = new MutationObserver(function() {
        paraBiriminiDegistir();
    });

    observer.observe(document.body, { 
        childList: true, 
        subtree: true,
        characterData: true
    });
})();


(function() {
    // Özel sınıfları kullanarak o div elementini bul
    const taxElement = document.querySelector('.footer-tax.rte.light');
    
    // Eğer element sayfada varsa tamamen sil
    if (taxElement) {
        taxElement.remove();
        console.log("Vergi ve kargo yazısı başarıyla silindi.");
    }
})();



// ==========================================
// 5. ÖDEME VE GÜVENLİK İKONLARI (TEMADAN BAĞIMSIZ YENİDEN YAZILDI)
// ==========================================
(function() {
    // Sadece ürün sayfalarında çalışması için kontrol
    if (!window.location.pathname.includes('/products/')) return;

    // Hedef: product-info-block alanlarının sonuncusunu veya en uygun taşıyıcıyı bul
    // Sepete ekle butonunun olduğu container
    const productBuyButtonDiv = document.querySelector('.product-buy-button');
    if (!productBuyButtonDiv) return;

    // Ödeme ve Güvenlik için Ana Kapsayıcı
    const paymentSecurityWrapper = document.createElement('div');
    paymentSecurityWrapper.className = 'product-payment-security product-info-block show-box color-scheme-cac6e78c-d0b5-474a-af4e-38455962e0e8 gradient';
    paymentSecurityWrapper.style.marginTop = '25px';

    // 1. Başlık ve Alt Metin
    paymentSecurityWrapper.innerHTML = `
        <div class="payment-security-title font-heading" style="font-weight: 700; margin-bottom: 8px;">Ödeme ve güvenlik</div>
        <div class="payment-security-description rte light" style="color: #6b7280; font-size: 0.9rem;">
            <p>Ödeme bilgileriniz güvenli bir şekilde işlenmektedir.</p>
        </div>
    `;

    // 2. Logoların Taşınacağı Flexbox Kapsayıcı
    const logosContainer = document.createElement('div');
    logosContainer.style.display = 'flex';
    logosContainer.style.alignItems = 'center'; 
    logosContainer.style.gap = '8px';          // Logolar arası boşluk
    logosContainer.style.marginTop = '12px';    
    logosContainer.style.flexWrap = 'wrap';    // Alt satıra taşabilme özelliği
    
    // Logoların Linkleri (Shopify Secure kaldırıldı, sadece kredi kartları var)
    const badgeUrls = [
        "https://cdn.shopify.com/s/files/1/0757/6053/3556/files/mastercard.png?v=1777533900", 
        "https://cdn.shopify.com/s/files/1/0757/6053/3556/files/visa.png?v=1777533900",
        "https://cdn.shopify.com/s/files/1/0757/6053/3556/files/iyzico-logo.webp?v=1778243999",
        "https://cdn.shopify.com/s/files/1/0757/6053/3556/files/troy.png?v=1777533900",    
        "https://cdn.shopify.com/s/files/1/0757/6053/3556/files/paraf.png?v=1777533900",   
        "https://cdn.shopify.com/s/files/1/0757/6053/3556/files/world.png?v=1777533900",   
        "https://cdn.shopify.com/s/files/1/0757/6053/3556/files/axess.png?v=1777533900",   
        "https://cdn.shopify.com/s/files/1/0757/6053/3556/files/maximum.png?v=1777533900"  
    ];

    // Logoları tek tek oluştur ve kapsayıcıya ekle
    badgeUrls.forEach(url => {
        const img = document.createElement('img');
        img.src = url;
        img.alt = "Payment Badge";
        
        // --- STANDART GÖRÜNÜM AYARLARI ---
        img.style.height = "40px"; 
        img.style.width = "auto";  // Orijinal en-boy oranını korur
        img.style.objectFit = "contain";
        
        // Çerçeve ve Arkaplan (Shopify logosuna benzer standart kutu görünümü)
        img.style.border = "1px solid #d2d0cc"; // Standart gri border rengi
        img.style.borderRadius = "4px";         // Hafif yuvarlak köşeler
        img.style.backgroundColor = "#ffffff";  // Arkaplan her zaman beyaz
        
        // İç Boşluk (Padding)
        img.style.padding = "8px 12px"; 
        
        // Kutu Modeli
        img.style.boxSizing = "border-box";
        img.style.display = "block"; // Görüntü hatalarını önlemek için

        logosContainer.appendChild(img);
    });

    // Logoları ana kapsayıcıya ekle
    paymentSecurityWrapper.appendChild(logosContainer);

    // Tüm bloğu, Sepete Ekle butonunun (ve onun hemen altında yer alacak diğer icon-text'lerin) en sonuna ekle.
    // Eğer sayfada .product-info-others div'i varsa onun hemen üstüne koymak en güvenlisidir.
    const productInfoOthers = document.querySelector('.product-info-others');
    if (productInfoOthers) {
        productInfoOthers.insertAdjacentElement('beforebegin', paymentSecurityWrapper);
    } else {
        // Eğer o div yoksa, doğrudan buy button'un arkasına tak
        productBuyButtonDiv.insertAdjacentElement('afterend', paymentSecurityWrapper);
    }
})();










    // upsell + product video başlangıç
// ==========================================
// ÜRÜN SAYFASI: VİDEO VE UPSELL BİRLEŞTİRİLMİŞ KOD (GÜNCELLENDİ: AYDINLIK TEMA VE YERLEŞİM)
// ==========================================
document.addEventListener("DOMContentLoaded", function() {
    if (!window.location.pathname.includes('/products/')) return;
    const currentHandle = window.location.pathname.split('/').filter(Boolean).pop().split('?')[0];

    // ==========================================
    // 1. REELS / VİDEO HARİTASI
    // ==========================================
    const videoMap = {
        "fifine-ampligame-sc8-7-1-surround-ses-efektli-rgb-xlr-usb-yayinci-ses-mikseri": [
            { url: "https://cdn.shopify.com/videos/c/o/v/749a43280fe04f2981c548e48943babf.mp4", title: "SC8", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/8ff0e4027322412b964e9584236a1d24.mp4", title: "SC8", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/1936a88245394ae3ba596c2dc40db7c1.mp4", title: "SC8", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampligame-sc3-ses-efektli-rgb-xlr-usb-oyuncu-ve-yayinci-mikseri": [
            { url: "https://cdn.shopify.com/videos/c/o/v/baecb66a7e1d4a30a245d3baf2755b06.mp4", title: "SC3", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/1ae190917e2a47a498b78556edc5698d.mp4", title: "SC3", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampligame-d6-stream-deck-15-tuslu-programlanabilir-yayin-kontrolcusu": [
            { url: "https://cdn.shopify.com/videos/c/o/v/dadfaeb7a5994a7aac2a78da8a452a59.mp4", title: "D6", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/ef03455a121a438b96fc951d9f451bb8.mp4", title: "D6", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/7d0572d44f3a4c01b5e71e0f7f7a6f45.mp4", title: "D6", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampligame-v3-4k-kayit-rgb-usb-yayinci-capture-card": [
            { url: "https://cdn.shopify.com/videos/c/o/v/031ca6a3afeb4e4998f08a4445535b68.mp4", title: "V3", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/002f46846c2f4261ab7c12425b0e6b9d.mp4", title: "V3", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampli1-xlr-ve-enstruman-girisli-usb-studyo-ses-karti": [
            { url: "https://cdn.shopify.com/videos/c/o/v/e4b3146bfaf14d6e99c3630d480b395b.mp4", title: "Ampli1", subtitle: "İncele &rarr;" }
        ],
        "fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu": [
            { url: "https://cdn.shopify.com/videos/c/o/v/bc2775e095a34790bd8a296cf8ff48eb.mp4", title: "K688", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/2b3da98e1a624e76844105d0ef09e019.mp4", title: "K688", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/dbedc21a25a146f3ab3e65cda3ae9933.mp4", title: "K688", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampligame-am8prot-rgb-standli-xlr-usb-dinamik-yayinci-mikrofon-seti": [
            { url: "https://cdn.shopify.com/videos/c/o/v/8f15c23f4dcb474a991759ef68888dc1.mp4", title: "AM8ProT", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/f454a9f9312647c292f84d76dc3d2773.mp4", title: "AM8ProT", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampligame-am8t-standli-xlr-usb-rgb-dinamik-yayinci-mikrofon-seti": [
            { url: "https://cdn.shopify.com/videos/c/o/v/271afd5f01914a359ba139a3c75b956c.mp4", title: "AM8T", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/5c50caef1e8f41e4b513ef3ef6d20857.mp4", title: "AM8T", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampligame-am8-xlr-usb-hibrit-rgb-dinamik-yayinci-mikrofonu": [
            { url: "https://cdn.shopify.com/videos/c/o/v/ff157fd413594c04a7c02570edf437e3.mp4", title: "AM8", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/fbb4d21bf3634e9ebaea4c0e96ba5252.mp4", title: "AM8", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/509c01a9d3234a719ff075552d3c8625.mp4", title: "AM8", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampligame-am6-rgb-usb-oyuncu-mikrofonu": [
            { url: "https://cdn.shopify.com/videos/c/o/v/d4312d946e214ee2b5a67b12f0223ced.mp4", title: "AM6", subtitle: "İncele &rarr;" }
        ],
        "fifine-amplitank-profile3-condenser-usb-yayinci-mikrofonu": [
            { url: "https://cdn.shopify.com/videos/c/o/v/cfffe5964ae94f8786cf48091f3d71f2.mp4", title: "Profile3", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/9f6853475e94403396abff66b5f451ab.mp4", title: "Profile3", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/4fbb5b2dece147e9b7f13503fbae227d.mp4", title: "Profile3", subtitle: "İncele &rarr;" }
        ],
        "fifine-m9-sarj-kutulu-2-kisilik-profesyonel-kablosuz-yaka-mikrofon-seti": [
            { url: "https://cdn.shopify.com/videos/c/o/v/76199f8386dd40ebba50667a1704e6c7.mp4", title: "M9", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/71a266ad6918476098441203cf727bdb.mp4", title: "M9", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/3b0bba8c44a14873bdf5ebfb22706f33.mp4", title: "M9", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampligame-h19-7-1-surround-eq-modlu-rgb-usb-oyuncu-kulakligi": [
            { url: "https://cdn.shopify.com/videos/c/o/v/6fdfb3d8b4ae45c6864c44142d68bc23.mp4", title: "H19", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/68bb015805b14eb1ad6bf6caeed5f466.mp4", title: "H19", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampligame-h13-7-1-surround-rgb-usb-oyuncu-kulakligi": [
            { url: "https://cdn.shopify.com/videos/c/o/v/0b5ef33b89594ec588b449ea9e500842.mp4", title: "H13", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/04740af7b6aa489794933b4d92359370.mp4", title: "H13", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/0e5725eae0c1412cb0946efb12862a41.mp4", title: "H13", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampligame-h9-7-1-surround-ses-kartli-mikrofonlu-profesyonel-oyuncu-kulakligi": [
            { url: "https://cdn.shopify.com/videos/c/o/v/a9890a87b9b744f7b10bb15f2da2dffa.mp4", title: "H9", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/11c47e44d1b54413b09399df0a5498ec.mp4", title: "H9", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/8e273988fcb346e59dcded02c6440c2a.mp4", title: "H9", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampligame-h6-mikrofonlu-7-1-surround-rgb-usb-oyuncu-ve-yayinci-kulakligi": [
            { url: "https://cdn.shopify.com/videos/c/o/v/9c605187c043479db595db3ff557de08.mp4", title: "H6", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/c6bcf078e78e42f5b0605a2f244616f8.mp4", title: "H6", subtitle: "İncele &rarr;" }
        ],
        "fifine-x3-aktif-gurultu-engelleyici-anc-mikrofonlu-kablosuz-bluetooth-kulak-ustu-kulaklik": [
            { url: "https://cdn.shopify.com/videos/c/o/v/a18e1755c59940d6a085aa2d5604bfa0.mp4", title: "X3", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/906df9c28a23423aa8b5aa7cc5393a9f.mp4", title: "X3", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/7234dcf73c484adda25e7832ab8e2f9c.mp4", title: "X3", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampligame-a22-rgb-2-1-subwooferli-usb-ve-bluetooth-oyuncu-bilgisayar-hoparloru": [
            { url: "https://cdn.shopify.com/videos/c/o/v/23bc999f6d304cb6bbe9de97d0187d6f.mp4", title: "A22", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampligame-a20-rgb-2-0-stereo-usb-ve-aux-girisli-oyuncu-bilgisayar-hoparloru": [
            { url: "https://cdn.shopify.com/videos/c/o/v/31d3744e48c8487591edf32e8d037f3d.mp4", title: "A20", subtitle: "İncele &rarr;" }
        ],
        "fifine-bm88-low-profile-profesyonel-masaustu-mikrofon-standi": [
            { url: "https://cdn.shopify.com/videos/c/o/v/748d9507497547a7834e24a0ece61352.mp4", title: "BM88", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/d2283fb9216342cfab483a3c02a75273.mp4", title: "BM88", subtitle: "İncele &rarr;" },
            { url: "https://cdn.shopify.com/videos/c/o/v/3bbf5f2653d64f4cbd752d3496b718e6.mp4", title: "BM88", subtitle: "İncele &rarr;" }
        ],
        "fifine-ampligame-bm38-rgb-aydinlatmali-profesyonel-boom-arm-mikrofon-standi": [
            { url: "https://cdn.shopify.com/videos/c/o/v/f0d3a6d20e5040faa0fab68bb887b5dd.mp4", title: "BM38", subtitle: "İncele &rarr;" }
        ]
    };

    // ==========================================
    // 2. TÜM UPSELL HARİTASI
    // ==========================================
    const upsellMap = {
        "fifine-ampligame-a6v-rgb-usb-oyuncu-ve-yayinci-condenser-mikrofonu": ["fifine-ampligame-a6t-boom-arm-standli-rgb-usb-yayinci-mikrofon-seti", "fifine-ampligame-a8-rgb-usb-podcast-ve-yayinci-mikrofonu", "fifine-ampligame-am6-rgb-usb-oyuncu-mikrofonu"],
        "fifine-ampligame-a8-rgb-usb-podcast-ve-yayinci-mikrofonu": ["fifine-ampligame-a8t-profesyonel-standli-rgb-usb-yayinci-mikrofon-seti", "fifine-ampligame-am8-xlr-usb-hibrit-rgb-dinamik-yayinci-mikrofonu", "fifine-k658-rgb-dinamik-usb-podcast-ve-bilgisayar-yayinci-mikrofonu"],
        "fifine-ampligame-a8t-profesyonel-standli-rgb-usb-yayinci-mikrofon-seti": ["fifine-ampligame-am8t-standli-xlr-usb-rgb-dinamik-yayinci-mikrofon-seti", "fifine-ampligame-am8prot-rgb-standli-xlr-usb-dinamik-yayinci-mikrofon-seti", "fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu"],
        "fifine-ampligame-a6t-boom-arm-standli-rgb-usb-yayinci-mikrofon-seti": ["fifine-ampligame-a8t-profesyonel-standli-rgb-usb-yayinci-mikrofon-seti", "fifine-ampligame-am8t-standli-xlr-usb-rgb-dinamik-yayinci-mikrofon-seti", "fifine-t669-profesyonel-boom-arm-standli-usb-condenser-yayinci-ve-podcast-mikrofon-seti"],
        "fifine-ampligame-a9-cok-yonlu-3-kapsullu-rgb-usb-yayinci-mikrofonu": ["fifine-ampligame-am8-xlr-usb-hibrit-rgb-dinamik-yayinci-mikrofonu", "fifine-ampligame-a8t-profesyonel-standli-rgb-usb-yayinci-mikrofon-seti", "fifine-ampligame-am9-rgb-usb-xlr-hibrit-dinamik-oyuncu-ve-yayinci-mikrofonu"],
        "fifine-ampligame-am6-rgb-usb-oyuncu-mikrofonu": ["fifine-ampligame-am8-xlr-usb-hibrit-rgb-dinamik-yayinci-mikrofonu", "fifine-ampligame-am9-rgb-usb-xlr-hibrit-dinamik-oyuncu-ve-yayinci-mikrofonu", "fifine-k658-rgb-dinamik-usb-podcast-ve-bilgisayar-yayinci-mikrofonu"],
        "fifine-ampligame-am8-xlr-usb-hibrit-rgb-dinamik-yayinci-mikrofonu": ["fifine-ampligame-am8prot-rgb-standli-xlr-usb-dinamik-yayinci-mikrofon-seti", "fifine-ampligame-am9-rgb-usb-xlr-hibrit-dinamik-oyuncu-ve-yayinci-mikrofonu", "fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu"],
        "fifine-ampligame-am8prot-rgb-standli-xlr-usb-dinamik-yayinci-mikrofon-seti": ["fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu", "fifine-amplitank-tank1-low-cut-filtreli-safkan-xlr-dinamik-studyo-mikrofonu", "fifine-ampligame-am9-rgb-usb-xlr-hibrit-dinamik-oyuncu-ve-yayinci-mikrofonu"],
        "fifine-ampligame-am8t-standli-xlr-usb-rgb-dinamik-yayinci-mikrofon-seti": ["fifine-ampligame-am8prot-rgb-standli-xlr-usb-dinamik-yayinci-mikrofon-seti", "fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu", "fifine-amplitank-tank1-low-cut-filtreli-safkan-xlr-dinamik-studyo-mikrofonu"],
        "fifine-ampligame-am9-rgb-usb-xlr-hibrit-dinamik-oyuncu-ve-yayinci-mikrofonu": ["fifine-ampligame-am8prot-rgb-standli-xlr-usb-dinamik-yayinci-mikrofon-seti", "fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu", "fifine-amplitank-tank1-low-cut-filtreli-safkan-xlr-dinamik-studyo-mikrofonu"],
        "fifine-k658-rgb-dinamik-usb-podcast-ve-bilgisayar-yayinci-mikrofonu": ["fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu", "fifine-ampligame-am9-rgb-usb-xlr-hibrit-dinamik-oyuncu-ve-yayinci-mikrofonu", "fifine-amplitank-tank1-low-cut-filtreli-safkan-xlr-dinamik-studyo-mikrofonu"],
        "fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu": ["fifine-amplitank-tank1-low-cut-filtreli-safkan-xlr-dinamik-studyo-mikrofonu", "fifine-ampligame-am9-rgb-usb-xlr-hibrit-dinamik-oyuncu-ve-yayinci-mikrofonu", "fifine-ampligame-am8prot-rgb-standli-xlr-usb-dinamik-yayinci-mikrofon-seti"],
        "fifine-amplitank-tank1-low-cut-filtreli-safkan-xlr-dinamik-studyo-mikrofonu": ["fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu", "fifine-ampligame-am9-rgb-usb-xlr-hibrit-dinamik-oyuncu-ve-yayinci-mikrofonu", "fifine-amplitank-profile3-condenser-usb-yayinci-mikrofonu"],
        "fifine-amplitank-profile3-condenser-usb-yayinci-mikrofonu": ["fifine-amplitank-tank1-low-cut-filtreli-safkan-xlr-dinamik-studyo-mikrofonu", "fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu", "fifine-ampligame-am9-rgb-usb-xlr-hibrit-dinamik-oyuncu-ve-yayinci-mikrofonu"],
        "fifine-t669-profesyonel-boom-arm-standli-usb-condenser-yayinci-ve-podcast-mikrofon-seti": ["fifine-t683-pop-filtre-ve-standli-condenser-usb-kayit-mikrofon-seti", "fifine-ampligame-a8t-profesyonel-standli-rgb-usb-yayinci-mikrofon-seti", "fifine-ampligame-am8t-standli-xlr-usb-rgb-dinamik-yayinci-mikrofon-seti"],
        "fifine-t683-pop-filtre-ve-standli-condenser-usb-kayit-mikrofon-seti": ["fifine-ampligame-a8t-profesyonel-standli-rgb-usb-yayinci-mikrofon-seti", "fifine-ampligame-am8t-standli-xlr-usb-rgb-dinamik-yayinci-mikrofon-seti", "fifine-ampligame-a6t-boom-arm-standli-rgb-usb-yayinci-mikrofon-seti"],
        "fifine-t732-profesyonel-boom-arm-ve-tripod-standli-usb-condenser-yayinci-mikrofon-seti": ["fifine-t669-profesyonel-boom-arm-standli-usb-condenser-yayinci-ve-podcast-mikrofon-seti", "fifine-t683-pop-filtre-ve-standli-condenser-usb-kayit-mikrofon-seti", "fifine-ampligame-a8t-profesyonel-standli-rgb-usb-yayinci-mikrofon-seti"],
        "fifine-k683a-pop-filtreli-type-c-ve-usb-condenser-yayinci-mikrofonu": ["fifine-k658-rgb-dinamik-usb-podcast-ve-bilgisayar-yayinci-mikrofonu", "fifine-ampligame-a8-rgb-usb-podcast-ve-yayinci-mikrofonu", "fifine-ampligame-am8-xlr-usb-hibrit-rgb-dinamik-yayinci-mikrofonu"],
        "fifine-k670b-sifir-gecikme-usb-condenser-podcast-ve-yayinci-mikrofonu": ["fifine-k683a-pop-filtreli-type-c-ve-usb-condenser-yayinci-mikrofonu", "fifine-k658-rgb-dinamik-usb-podcast-ve-bilgisayar-yayinci-mikrofonu", "fifine-ampligame-a8-rgb-usb-podcast-ve-yayinci-mikrofonu"],
        "fifine-k669b-studyo-ve-yayinci-usb-condenser-bilgisayar-mikrofonu": ["fifine-k670b-sifir-gecikme-usb-condenser-podcast-ve-yayinci-mikrofonu", "fifine-ampligame-a6v-rgb-usb-oyuncu-ve-yayinci-condenser-mikrofonu", "fifine-ampligame-a8-rgb-usb-podcast-ve-yayinci-mikrofonu"],
        "fifine-m6-kablosuz-yaka-mikrofonu-type-c-telefon-ve-pc-uyumlu": ["fifine-m8-cift-kisilik-kablosuz-yaka-mikrofonu-seti-type-c-uyumlu", "fifine-m9-sarj-kutulu-2-kisilik-profesyonel-kablosuz-yaka-mikrofon-seti", "fifine-k053-sifir-gecikme-kardioid-usb-yayinci-ve-bilgisayar-yaka-mikrofonu"],
        "fifine-m8-cift-kisilik-kablosuz-yaka-mikrofonu-seti-type-c-uyumlu": ["fifine-m9-sarj-kutulu-2-kisilik-profesyonel-kablosuz-yaka-mikrofon-seti", "fifine-m6-kablosuz-yaka-mikrofonu-type-c-telefon-ve-pc-uyumlu", "fifine-k053-sifir-gecikme-kardioid-usb-yayinci-ve-bilgisayar-yaka-mikrofonu"],
        "fifine-m9-sarj-kutulu-2-kisilik-profesyonel-kablosuz-yaka-mikrofon-seti": ["fifine-m8-cift-kisilik-kablosuz-yaka-mikrofonu-seti-type-c-uyumlu", "fifine-m6-kablosuz-yaka-mikrofonu-type-c-telefon-ve-pc-uyumlu", "fifine-k053-sifir-gecikme-kardioid-usb-yayinci-ve-bilgisayar-yaka-mikrofonu"],
        "fifine-k053-sifir-gecikme-kardioid-usb-yayinci-ve-bilgisayar-yaka-mikrofonu": ["fifine-m6-kablosuz-yaka-mikrofonu-type-c-telefon-ve-pc-uyumlu", "fifine-m8-cift-kisilik-kablosuz-yaka-mikrofonu-seti-type-c-uyumlu", "fifine-m9-sarj-kutulu-2-kisilik-profesyonel-kablosuz-yaka-mikrofon-seti"],
        "fifine-ampligame-h3-cikarilabilir-mikrofonlu-3-5mm-pc-ve-konsol-uyumlu-rgb-oyuncu-kulakligi": ["fifine-ampligame-h9-7-1-surround-ses-kartli-mikrofonlu-profesyonel-oyuncu-kulakligi", "fifine-ampligame-h16-7-1-surround-usb-3-5mm-rgb-oyuncu-kulakligi", "fifine-ampligame-h6-mikrofonlu-7-1-surround-rgb-usb-oyuncu-ve-yayinci-kulakligi"],
        "fifine-ampligame-h6-mikrofonlu-7-1-surround-rgb-usb-oyuncu-ve-yayinci-kulakligi": ["fifine-ampligame-h9-7-1-surround-ses-kartli-mikrofonlu-profesyonel-oyuncu-kulakligi", "fifine-ampligame-h19-7-1-surround-eq-modlu-rgb-usb-oyuncu-kulakligi", "fifine-ampligame-h16-7-1-surround-usb-3-5mm-rgb-oyuncu-kulakligi"],
        "fifine-ampligame-h13-7-1-surround-rgb-usb-oyuncu-kulakligi": ["fifine-ampligame-h16-7-1-surround-usb-3-5mm-rgb-oyuncu-kulakligi", "fifine-ampligame-h19-7-1-surround-eq-modlu-rgb-usb-oyuncu-kulakligi", "fifine-ampligame-h9-7-1-surround-ses-kartli-mikrofonlu-profesyonel-oyuncu-kulakligi"],
        "fifine-ampligame-h16-7-1-surround-usb-3-5mm-rgb-oyuncu-kulakligi": ["fifine-ampligame-h19-7-1-surround-eq-modlu-rgb-usb-oyuncu-kulakligi", "fifine-h8-studyo-referans-oyuncu-kulakligi", "fifine-ampligame-h9-7-1-surround-ses-kartli-mikrofonlu-profesyonel-oyuncu-kulakligi"],
        "fifine-ampligame-h9-7-1-surround-ses-kartli-mikrofonlu-profesyonel-oyuncu-kulakligi": ["fifine-ampligame-h19-7-1-surround-eq-modlu-rgb-usb-oyuncu-kulakligi", "fifine-h8-studyo-referans-oyuncu-kulakligi", "fifine-ampligame-h16-7-1-surround-usb-3-5mm-rgb-oyuncu-kulakligi"],
        "fifine-ampligame-h19-7-1-surround-eq-modlu-rgb-usb-oyuncu-kulakligi": ["fifine-h8-studyo-referans-oyuncu-kulakligi", "fifine-ampligame-h9-7-1-surround-ses-kartli-mikrofonlu-profesyonel-oyuncu-kulakligi", "fifine-ampligame-h16-7-1-surround-usb-3-5mm-rgb-oyuncu-kulakligi"],
        "fifine-h8-studyo-referans-oyuncu-kulakligi": ["fifine-ampligame-h19-7-1-surround-eq-modlu-rgb-usb-oyuncu-kulakligi", "fifine-x3-aktif-gurultu-engelleyici-anc-mikrofonlu-kablosuz-bluetooth-kulak-ustu-kulaklik", "fifine-ampligame-h9-7-1-surround-ses-kartli-mikrofonlu-profesyonel-oyuncu-kulakligi"],
        "fifine-x3-aktif-gurultu-engelleyici-anc-mikrofonlu-kablosuz-bluetooth-kulak-ustu-kulaklik": ["fifine-h8-studyo-referans-oyuncu-kulakligi", "fifine-ampligame-h19-7-1-surround-eq-modlu-rgb-usb-oyuncu-kulakligi", "fifine-ampligame-h9-7-1-surround-ses-kartli-mikrofonlu-profesyonel-oyuncu-kulakligi"],
        "fifine-ampligame-a20-rgb-2-0-stereo-usb-ve-aux-girisli-oyuncu-bilgisayar-hoparloru": ["fifine-ampligame-a22-rgb-2-1-subwooferli-usb-ve-bluetooth-oyuncu-bilgisayar-hoparloru", "fifine-ampligame-h16-7-1-surround-usb-3-5mm-rgb-oyuncu-kulakligi", "fifine-ampligame-h3-cikarilabilir-mikrofonlu-3-5mm-pc-ve-konsol-uyumlu-rgb-oyuncu-kulakligi"],
        "fifine-ampligame-a22-rgb-2-1-subwooferli-usb-ve-bluetooth-oyuncu-bilgisayar-hoparloru": ["fifine-ampligame-h19-7-1-surround-eq-modlu-rgb-usb-oyuncu-kulakligi", "fifine-h8-studyo-referans-oyuncu-kulakligi", "fifine-ampligame-h16-7-1-surround-usb-3-5mm-rgb-oyuncu-kulakligi"],
        "fifine-ampligame-sc3-ses-efektli-rgb-xlr-usb-oyuncu-ve-yayinci-mikseri": ["fifine-ampligame-sc8-7-1-surround-ses-efektli-rgb-xlr-usb-yayinci-ses-mikseri", "fifine-ampligame-d6-stream-deck-15-tuslu-programlanabilir-yayin-kontrolcusu", "fifine-ampli1-xlr-ve-enstruman-girisli-usb-studyo-ses-karti"],
        "fifine-ampligame-sc8-7-1-surround-ses-efektli-rgb-xlr-usb-yayinci-ses-mikseri": ["fifine-ampligame-d6-stream-deck-15-tuslu-programlanabilir-yayin-kontrolcusu", "fifine-amplitank-ampli3-cift-kanalli-xlr-studyo-kayit-usb-ses-karti", "fifine-ampligame-v3-4k-kayit-rgb-usb-yayinci-capture-card"],
        "fifine-ampli1-xlr-ve-enstruman-girisli-usb-studyo-ses-karti": ["fifine-amplitank-ampli3-cift-kanalli-xlr-studyo-kayit-usb-ses-karti", "fifine-ampligame-sc3-ses-efektli-rgb-xlr-usb-oyuncu-ve-yayinci-mikseri", "fifine-ampligame-sc8-7-1-surround-ses-efektli-rgb-xlr-usb-yayinci-ses-mikseri"],
        "fifine-amplitank-ampli3-cift-kanalli-xlr-studyo-kayit-usb-ses-karti": ["fifine-ampli1-xlr-ve-enstruman-girisli-usb-studyo-ses-karti", "fifine-ampligame-sc8-7-1-surround-ses-efektli-rgb-xlr-usb-yayinci-ses-mikseri", "fifine-ampligame-d6-stream-deck-15-tuslu-programlanabilir-yayin-kontrolcusu"],
        "fifine-ampligame-d6-stream-deck-15-tuslu-programlanabilir-yayin-kontrolcusu": ["fifine-ampligame-sc8-7-1-surround-ses-efektli-rgb-xlr-usb-yayinci-ses-mikseri", "fifine-ampligame-sc3-ses-efektli-rgb-xlr-usb-oyuncu-ve-yayinci-mikseri", "fifine-ampligame-v3-4k-kayit-rgb-usb-yayinci-capture-card"],
        "fifine-ampligame-v3-4k-kayit-rgb-usb-yayinci-capture-card": ["fifine-ampligame-d6-stream-deck-15-tuslu-programlanabilir-yayin-kontrolcusu", "fifine-ampligame-sc3-ses-efektli-rgb-xlr-usb-oyuncu-ve-yayinci-mikseri", "fifine-ampligame-sc8-7-1-surround-ses-efektli-rgb-xlr-usb-yayinci-ses-mikseri"],
        "fifine-ampligame-bm38-rgb-aydinlatmali-profesyonel-boom-arm-mikrofon-standi": ["fifine-bm88-low-profile-profesyonel-masaustu-mikrofon-standi", "fifine-bm66-premium-agir-yuk-tasiyici-360-masaustu-mikrofon-kolu", "fifine-pf1-cift-katmanli-esnek-boyunlu-profesyonel-mikrofon-pop-filtre"],
        "fifine-bm88-low-profile-profesyonel-masaustu-mikrofon-standi": ["fifine-bm66-premium-agir-yuk-tasiyici-360-masaustu-mikrofon-kolu", "fifine-ampligame-bm38-rgb-aydinlatmali-profesyonel-boom-arm-mikrofon-standi", "fifine-sk1-titresim-onleyici-shock-mount-mikrofon-tutucu"],
        "fifine-bm66-premium-agir-yuk-tasiyici-360-masaustu-mikrofon-kolu": ["fifine-bm88-low-profile-profesyonel-masaustu-mikrofon-standi", "fifine-ampligame-bm38-rgb-aydinlatmali-profesyonel-boom-arm-mikrofon-standi", "fifine-u1-u-sekilli-evrensel-metal-mikrofon-pop-filtresi"],
        "fifine-bm63-profesyonel-masa-tipi-yayli-akrobat-mikrofon-standi": ["fifine-cs1-masa-tipi-katlanabilir-akrobat-mikrofon-standi-ve-sehpasi", "fifine-bm66-premium-agir-yuk-tasiyici-360-masaustu-mikrofon-kolu", "fifine-bm88-low-profile-profesyonel-masaustu-mikrofon-standi"],
        "fifine-cs1-masa-tipi-katlanabilir-akrobat-mikrofon-standi-ve-sehpasi": ["fifine-bm88-low-profile-profesyonel-masaustu-mikrofon-standi", "fifine-bm66-premium-agir-yuk-tasiyici-360-masaustu-mikrofon-kolu", "fifine-u1-u-sekilli-evrensel-metal-mikrofon-pop-filtresi"],
        "fifine-dp1-metal-masaustu-tripod-mikrofon-standi-ve-ayakligi": ["fifine-bm63-profesyonel-masa-tipi-yayli-akrobat-mikrofon-standi", "fifine-cs1-masa-tipi-katlanabilir-akrobat-mikrofon-standi-ve-sehpasi", "fifine-u1-u-sekilli-evrensel-metal-mikrofon-pop-filtresi"],
        "fifine-sk1-titresim-onleyici-shock-mount-mikrofon-tutucu": ["fifine-u1-u-sekilli-evrensel-metal-mikrofon-pop-filtresi", "fifine-pf1-cift-katmanli-esnek-boyunlu-profesyonel-mikrofon-pop-filtre", "fifine-cs1-masa-tipi-katlanabilir-akrobat-mikrofon-standi-ve-sehpasi"],
        "fifine-pf1-cift-katmanli-esnek-boyunlu-profesyonel-mikrofon-pop-filtre": ["fifine-u1-u-sekilli-evrensel-metal-mikrofon-pop-filtresi", "fifine-sk1-titresim-onleyici-shock-mount-mikrofon-tutucu", "fifine-bm63-profesyonel-masa-tipi-yayli-akrobat-mikrofon-standi"],
        "fifine-u1-u-sekilli-evrensel-metal-mikrofon-pop-filtresi": ["fifine-pf1-cift-katmanli-esnek-boyunlu-profesyonel-mikrofon-pop-filtre", "fifine-sk1-titresim-onleyici-shock-mount-mikrofon-tutucu", "fifine-cs1-masa-tipi-katlanabilir-akrobat-mikrofon-standi-ve-sehpasi"],
        "fifine-ampligame-a6-a6v-a6t-serisi-uyumlu-yedek-pop-filtre": ["fifine-ampligame-a6t-boom-arm-standli-rgb-usb-yayinci-mikrofon-seti", "fifine-pf1-cift-katmanli-esnek-boyunlu-profesyonel-mikrofon-pop-filtre", "fifine-u1-u-sekilli-evrensel-metal-mikrofon-pop-filtresi"],
        "fifine-l9-3-metre-profesyonel-altin-kaplama-3-pin-xlr-mikrofon-ses-kablosu": ["fifine-ampligame-sc8-7-1-surround-ses-efektli-rgb-xlr-usb-yayinci-ses-mikseri", "fifine-ampligame-sc3-ses-efektli-rgb-xlr-usb-oyuncu-ve-yayinci-mikseri", "fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu"]
    };

    // İlgili ürün için haritaları kontrol et
    const productVideos = videoMap[currentHandle];
    const productUpsells = upsellMap[currentHandle];

    // Hedef div: Ödeme ve Güvenlik bilgilerinin ("product-payment-security") HEMEN ÜSTÜNDE olacak
    const targetDiv = document.querySelector('.product-payment-security');
    if (!targetDiv) return;

    // Her ikisini (Videoları ve Upsell'i) sarmalayacak ana kapsayıcı
    const customWidgetsWrapper = document.createElement('div');
    customWidgetsWrapper.className = 'ff-custom-widgets-wrapper';
    
    // YENİ CSS: Sitenin aydınlık temasına uyumlu Upsell ve kompakt Videolar
    const customStyle = document.createElement('style');
    customStyle.innerHTML = `
        .ff-custom-widgets-wrapper { margin-top: 25px; margin-bottom: 25px; width: 100%; clear: both; display: flex; flex-direction: column; gap: 20px; }
        .ff-custom-widgets-wrapper p { color: #111827 !important; font-size: 1.2rem !important; font-weight: 700; margin-bottom: 12px; margin-top: 0; letter-spacing: 0.5px; border: none; padding: 0; }
        
        /* --------------------------
           VİDEO BÖLÜMÜ (COMPACT)
        ----------------------------- */
        .ff-sc8-reels-grid { display: flex; flex-wrap: wrap; gap: 12px; justify-content: flex-start; }
        .ff-sc8-reel-card { width: 140px; height: 248px; background: #000; border-radius: 14px; position: relative; overflow: hidden; cursor: pointer; transition: transform 0.3s ease, box-shadow 0.3s ease; box-shadow: 0 4px 10px rgba(0,0,0,0.1); text-decoration: none !important; flex-shrink: 0; }
        .ff-sc8-reel-card:hover { transform: translateY(-4px); box-shadow: 0 8px 20px rgba(90, 32, 223, 0.25); }
        .ff-sc8-reel-card video { position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; }
        .ff-sc8-reel-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 25%, transparent 75%, rgba(0,0,0,0.6) 100%); pointer-events: none; }
        .ff-sc8-reel-title { position: absolute; top: 12px; left: 0; width: 100%; text-align: center; color: #fff !important; font-size: 1.1rem !important; font-weight: 800 !important; text-shadow: 0 2px 4px rgba(0,0,0,0.8); margin: 0 !important; padding: 0 5px; box-sizing: border-box; }
        .ff-sc8-reel-subtitle { position: absolute; bottom: 12px; left: 0; width: 100%; text-align: center; color: #e5e7eb; font-size: 0.8rem; font-weight: 500; text-shadow: 0 1px 3px rgba(0,0,0,0.8); }
        .ff-reel-play-btn { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 44px; height: 44px; background: rgba(255,255,255,0.3); backdrop-filter: blur(4px); border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: background 0.3s; }
        .ff-sc8-reel-card:hover .ff-reel-play-btn { background: rgba(90, 32, 223, 0.9); }
        .ff-reel-play-btn svg { width: 20px; height: 20px; fill: #fff; margin-left: 3px; }

        /* Lightbox (Büyüyen Video İçin) */
        .ff-reel-lightbox-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.85); z-index: 999999; display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.3s ease; cursor: pointer; }
        .ff-reel-lightbox-overlay.active { opacity: 1; }
        .ff-reel-lightbox-content { background: #000; width: 100%; max-width: 400px; height: 90vh; border-radius: 20px; overflow: hidden; box-shadow: 0 30px 60px rgba(0,0,0,0.4); position: relative; display: flex; align-items: center; justify-content: center; cursor: auto; }
        .ff-reel-lightbox-video { width: 100%; height: 100%; object-fit: cover; }
        .ff-reel-lightbox-close { position: absolute; top: 20px; left: 20px; background: rgba(255,255,255,0.2); color: #fff; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 24px; cursor: pointer; transition: all 0.3s ease; z-index: 10; }
        .ff-reel-lightbox-close:hover { background: rgba(255,255,255,0.4); transform: scale(1.1); }

        /* --------------------------
           UPSELL BÖLÜMÜ (AYDINLIK TEMAYA UYUMLU, DİKEY VE KÜÇÜK)
        ----------------------------- */
        .ff-upsell-section { padding-top: 15px; border-top: 1px solid #e5e7eb; margin-top: 5px; } /* Videolardan ayırmak için ince çizgi */
        .ff-upsell-grid { display: flex; flex-wrap: wrap; gap: 12px; }
        .ff-upsell-card { 
            background: #ffffff; /* Tema ile uyumlu beyaz arkaplan */
            border: 1px solid #e5e7eb; /* Tema ile uyumlu hafif gri border */
            border-radius: 10px; 
            padding: 12px 10px; 
            text-decoration: none !important; 
            display: flex; 
            flex-direction: column; 
            align-items: center; 
            justify-content: center; 
            transition: all 0.3s ease; 
            width: calc(33.33% - 8px); 
            min-width: 90px;
            box-sizing: border-box;
        }
        .ff-upsell-card:hover { border-color: #5A20DF; transform: translateY(-3px); box-shadow: 0 6px 15px rgba(90, 32, 223, 0.1); }
        .ff-upsell-card img { width: 100%; max-width: 65px; height: 65px; object-fit: contain; margin-bottom: 10px; transition: transform 0.3s ease; }
        .ff-upsell-card:hover img { transform: scale(1.1); }
        .ff-upsell-card h4 { color: #111827 !important; font-size: 0.8rem !important; margin: 0 !important; font-weight: 600 !important; text-align: center; letter-spacing: 0.3px; line-height: 1.2; }

        @media (max-width: 768px) {
            /* Mobil Video Ayarları */
            .ff-sc8-reels-grid { flex-wrap: nowrap; overflow-x: auto; -ms-overflow-style: none; scrollbar-width: none; scroll-snap-type: x mandatory; padding-bottom: 5px;}
            .ff-sc8-reels-grid::-webkit-scrollbar { display: none; }
            .ff-sc8-reel-card { width: 130px; height: 230px; scroll-snap-align: start; }
            .ff-reel-lightbox-content { max-width: 320px; height: 80vh; }
            
            /* Mobil Upsell Ayarları */
            .ff-upsell-grid { gap: 8px; } 
            .ff-upsell-card { width: calc(33.33% - 6px); padding: 10px 5px; }
            .ff-upsell-card h4 { font-size: 0.7rem !important; }
            .ff-upsell-card img { max-width: 50px; height: 50px; margin-bottom: 8px; }
        }
    `;
    customWidgetsWrapper.appendChild(customStyle);
    targetDiv.insertAdjacentElement('beforebegin', customWidgetsWrapper);

    // ==========================================
    // 3. VİDEOLARI OLUŞTUR (ÜSTTE YER ALACAK)
    // ==========================================
    if (productVideos && productVideos.length > 0) {
        const reelsWrapper = document.createElement('div');
        let cardsHtml = '';
        productVideos.forEach(video => {
            cardsHtml += `
                <a href="#" class="ff-sc8-reel-card ff-reel-clickable" data-reel-url="${video.url}">
                    <video muted playsinline preload="metadata">
                        <source src="${video.url}#t=0.001" type="video/mp4">
                    </video>
                    <div class="ff-sc8-reel-overlay"></div>
                    <h4 class="ff-sc8-reel-title">${video.title}</h4>
                    <div class="ff-reel-play-btn">
                        <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    </div>
                    <span class="ff-sc8-reel-subtitle">${video.subtitle}</span>
                </a>
            `;
        });
        reelsWrapper.innerHTML = `
            <p>Ürünü Yakından İnceleyin</p>
            <div class="ff-sc8-reels-grid">${cardsHtml}</div>
        `;
        customWidgetsWrapper.appendChild(reelsWrapper);

        // Lightbox Eventleri
        const clickableCards = reelsWrapper.querySelectorAll('.ff-reel-clickable');
        clickableCards.forEach(card => {
            card.addEventListener('click', function(e) {
                e.preventDefault(); 
                const videoUrl = this.getAttribute('data-reel-url');
                openLightbox(videoUrl);
            });
        });

        function openLightbox(videoUrl) {
            const overlay = document.createElement('div');
            overlay.className = 'ff-reel-lightbox-overlay';
            const content = document.createElement('div');
            content.className = 'ff-reel-lightbox-content';
            const closeBtn = document.createElement('div');
            closeBtn.className = 'ff-reel-lightbox-close';
            closeBtn.innerHTML = '&times;';
            content.appendChild(closeBtn);
            const lightboxVideo = document.createElement('video');
            lightboxVideo.className = 'ff-reel-lightbox-video';
            lightboxVideo.controls = true; 
            lightboxVideo.playsinline = true; 
            lightboxVideo.autoplay = true; 
            lightboxVideo.preload = 'metadata';
            const videoSource = document.createElement('source');
            videoSource.src = videoUrl;
            videoSource.type = 'video/mp4';
            lightboxVideo.appendChild(videoSource);
            lightboxVideo.muted = false;
            content.appendChild(lightboxVideo);
            overlay.appendChild(content);
            document.body.appendChild(overlay);

            const closeLightbox = () => {
                overlay.classList.remove('active');
                lightboxVideo.pause(); 
                setTimeout(() => { document.body.removeChild(overlay); }, 300);
            };
            closeBtn.addEventListener('click', function(e) { e.stopPropagation(); closeLightbox(); });
            overlay.addEventListener('click', closeLightbox);
            content.addEventListener('click', function(e) { e.stopPropagation(); });
            setTimeout(() => { overlay.classList.add('active'); }, 10);
        }
    }

    // ==========================================
    // 4. UPSELL KARTLARINI OLUŞTUR (VİDEOLARIN ALTINDA YER ALACAK)
    // ==========================================
    if (productUpsells && productUpsells.length > 0) {
        const upsellWrapper = document.createElement('div');
        upsellWrapper.className = 'ff-upsell-section'; // Videolardan ayırmak için yeni class
        upsellWrapper.innerHTML = `
            <p style="color: #111827 !important; font-size: 1.2rem !important; font-weight: 700; margin-bottom: 12px; margin-top: 0; letter-spacing: 0.5px; border: none; padding: 0;">Bunları Da İnceleyebilirsiniz</p>
            <div class="ff-upsell-grid"><span style="color:#6b7280; font-size:0.8rem;">Yükleniyor...</span></div>
        `;
        customWidgetsWrapper.appendChild(upsellWrapper);

        const gridObj = upsellWrapper.querySelector('.ff-upsell-grid');

        Promise.all(productUpsells.map(handle => 
            fetch('/products/' + handle + '.js').then(res => res.json())
        )).then(products => {
            let htmlContent = '';
            products.forEach(p => {
                let cleanTitle = p.title.replace(/Fifine /i, '')
                                        .replace(/Ampligame /i, '')
                                        .replace(/AmpliTank /i, '')
                                        .replace(/3 Metre Profesyonel Altın Kaplama 3-Pin XLR Mikrofon Ses Kablosu/i, 'L9 Kablo') 
                                        .trim().split(' ')[0]; 
                
                // DİKEY KART TASARIMI (Aydınlık tema)
                htmlContent += `
                    <a href="${p.url}" class="ff-upsell-card">
                        <img src="${p.featured_image}" alt="${cleanTitle}">
                        <h4>${cleanTitle}</h4>
                    </a>
                `;
            });
            gridObj.innerHTML = htmlContent;
        }).catch(err => {
            upsellWrapper.style.display = 'none';
        });
    }
});
    // upsell + product video bitiş



// ana sayfa reels slider
document.addEventListener("DOMContentLoaded", function() {
    // SADECE ANA SAYFADA ÇALIŞMASINI SAĞLA
    if (window.location.pathname !== '/' && window.location.pathname !== '/tr') return;

    // Hedef div'i bul (Oyuncu Yorumları kapsayıcısı)
    const testimonialWrapper = document.querySelector('.testimonial__wrapper');
    if (!testimonialWrapper) return;

    // ==========================================
    // ANA SAYFA VİDEO VİTRİNİ VERİLERİ (17 ÜRÜN)
    // ==========================================
    const homeReelsData = [
        { title: "Fifine Ampligame SC8 7.1 Surround Ses Efektli RGB XLR/USB Yayıncı Ses Mikseri", url: "/products/fifine-ampligame-sc8-7-1-surround-ses-efektli-rgb-xlr-usb-yayinci-ses-mikseri", video: "https://cdn.shopify.com/videos/c/o/v/749a43280fe04f2981c548e48943babf.mp4" },
        { title: "Fifine Ampligame SC3 Ses Efektli RGB XLR/USB Oyuncu ve Yayıncı Mikseri", url: "/products/fifine-ampligame-sc3-ses-efektli-rgb-xlr-usb-oyuncu-ve-yayinci-mikseri", video: "https://cdn.shopify.com/videos/c/o/v/baecb66a7e1d4a30a245d3baf2755b06.mp4" },
        { title: "Fifine Ampligame D6 Stream Deck 15 Tuşlu Yayın Kontrolcüsü", url: "/products/fifine-ampligame-d6-stream-deck-15-tuslu-programlanabilir-yayin-kontrolcusu", video: "https://cdn.shopify.com/videos/c/o/v/dadfaeb7a5994a7aac2a78da8a452a59.mp4" },
        { title: "Fifine K688 XLR/USB Hibrit Dinamik Stüdyo Podcast Mikrofonu", url: "/products/fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu", video: "https://cdn.shopify.com/videos/c/o/v/bc2775e095a34790bd8a296cf8ff48eb.mp4" },
        { title: "Fifine Ampligame AM8ProT RGB Standlı XLR/USB Dinamik Yayıncı Mikrofon Seti", url: "/products/fifine-ampligame-am8prot-rgb-standli-xlr-usb-dinamik-yayinci-mikrofon-seti", video: "https://cdn.shopify.com/videos/c/o/v/8f15c23f4dcb474a991759ef68888dc1.mp4" },
        { title: "Fifine M9 Şarj Kutulu 2 Kişilik Profesyonel Kablosuz Yaka Mikrofon Seti", url: "/products/fifine-m9-sarj-kutulu-2-kisilik-profesyonel-kablosuz-yaka-mikrofon-seti", video: "https://cdn.shopify.com/videos/c/o/v/76199f8386dd40ebba50667a1704e6c7.mp4" },
        { title: "Fifine Ampligame H19 7.1 Surround EQ Modlu RGB USB Oyuncu Kulaklığı", url: "/products/fifine-ampligame-h19-7-1-surround-eq-modlu-rgb-usb-oyuncu-kulakligi", video: "https://cdn.shopify.com/videos/c/o/v/6fdfb3d8b4ae45c6864c44142d68bc23.mp4" },
        { title: "Fifine Ampligame AM8T Standlı XLR/USB RGB Dinamik Yayıncı Mikrofon Seti", url: "/products/fifine-ampligame-am8t-standli-xlr-usb-rgb-dinamik-yayinci-mikrofon-seti", video: "https://cdn.shopify.com/videos/c/o/v/271afd5f01914a359ba139a3c75b956c.mp4" },
        { title: "Fifine Ampligame AM8 XLR/USB Hibrit RGB Dinamik Yayıncı Mikrofonu", url: "/products/fifine-ampligame-am8-xlr-usb-hibrit-rgb-dinamik-yayinci-mikrofonu", video: "https://cdn.shopify.com/videos/c/o/v/ff157fd413594c04a7c02570edf437e3.mp4" },
        { title: "Fifine Ampligame H13 7.1 Surround RGB USB Oyuncu Kulaklığı", url: "/products/fifine-ampligame-h13-7-1-surround-rgb-usb-oyuncu-kulakligi", video: "https://cdn.shopify.com/videos/c/o/v/0b5ef33b89594ec588b449ea9e500842.mp4" },
        { title: "Fifine BM38 RGB Aydınlatmalı Profesyonel Boom Arm Mikrofon Standı", url: "/products/fifine-ampligame-bm38-rgb-aydinlatmali-profesyonel-boom-arm-mikrofon-standi", video: "https://cdn.shopify.com/videos/c/o/v/f0d3a6d20e5040faa0fab68bb887b5dd.mp4" },
        { title: "Fifine BM88 Low Profile Profesyonel Masaüstü Mikrofon Standı", url: "/products/fifine-bm88-low-profile-profesyonel-masaustu-mikrofon-standi", video: "https://cdn.shopify.com/videos/c/o/v/748d9507497547a7834e24a0ece61352.mp4" },
        { title: "Fifine Ampligame V3 4K Kayıt RGB USB Yayıncı Capture Card", url: "/products/fifine-ampligame-v3-4k-kayit-rgb-usb-yayinci-capture-card", video: "https://cdn.shopify.com/videos/c/o/v/031ca6a3afeb4e4998f08a4445535b68.mp4" },
        { title: "Fifine X3 Aktif Gürültü Engelleyici (ANC) Bluetooth Kulaklık", url: "/products/fifine-x3-aktif-gurultu-engelleyici-anc-mikrofonlu-kablosuz-bluetooth-kulak-ustu-kulaklik", video: "https://cdn.shopify.com/videos/c/o/v/a18e1755c59940d6a085aa2d5604bfa0.mp4" },
        { title: "Fifine Ampligame AM6 RGB USB Oyuncu Mikrofonu", url: "/products/fifine-ampligame-am6-rgb-usb-oyuncu-mikrofonu", video: "https://cdn.shopify.com/videos/c/o/v/d4312d946e214ee2b5a67b12f0223ced.mp4" },
        { title: "Fifine Ampligame H9 7.1 Surround Ses Kartlı Profesyonel Oyuncu Kulaklığı", url: "/products/fifine-ampligame-h9-7-1-surround-ses-kartli-mikrofonlu-profesyonel-oyuncu-kulakligi", video: "https://cdn.shopify.com/videos/c/o/v/a9890a87b9b744f7b10bb15f2da2dffa.mp4" },
        { title: "Fifine AmpliTank Profile3 Condenser USB Yayıncı Mikrofonu", url: "/products/fifine-amplitank-profile3-condenser-usb-yayinci-mikrofonu", video: "https://cdn.shopify.com/videos/c/o/v/cfffe5964ae94f8786cf48091f3d71f2.mp4" }
    ];

    // CSS Tasarım
    const hpStyle = document.createElement('style');
    hpStyle.innerHTML = `
        .ff-hp-reels-section { width: 100%; max-width: 1400px; margin: 60px auto 20px auto; padding: 0 20px; overflow: hidden; position: relative; }
        .ff-hp-reels-header { text-align: center; margin-bottom: 30px; }
        .ff-hp-reels-header h2 { font-size: 2.2rem; font-weight: 800; color: #ffffff; margin-bottom: 8px; }
        .ff-hp-reels-header p { font-size: 1rem; color: #9ca3af; margin: 0; }
        
        .ff-slider-wrapper { position: relative; display: flex; align-items: center; }

        /* Kayan Oklar */
        .ff-hp-arrow { position: absolute; top: 50%; transform: translateY(-50%); width: 44px; height: 44px; background: rgba(0,0,0,0.6); color: #fff; border: 1px solid rgba(255,255,255,0.1); border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 10; backdrop-filter: blur(4px); transition: all 0.3s; }
        .ff-hp-arrow:hover { background: #5d0be4; border-color: #5d0be4; }
        .ff-hp-arrow.prev { left: -10px; }
        .ff-hp-arrow.next { right: -10px; }
        .ff-hp-arrow svg { width: 22px; height: 22px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

        /* Slider */
        .ff-hp-reels-slider { display: flex; gap: 16px; overflow-x: auto; padding-bottom: 20px; scroll-snap-type: x mandatory; cursor: grab; -ms-overflow-style: none; scrollbar-width: none; flex-grow: 1; }
        .ff-hp-reels-slider::-webkit-scrollbar { display: none; }
        .ff-hp-reels-slider:active { cursor: grabbing; }

        /* Kart Tasarımı */
        .ff-hp-reel-card { flex: 0 0 260px; height: 460px; border-radius: 16px; overflow: hidden; position: relative; background: #000; cursor: pointer; scroll-snap-align: start; box-shadow: 0 10px 25px rgba(0,0,0,0.5); transition: transform 0.3s ease; user-select: none; }
        .ff-hp-reel-card:hover { transform: translateY(-5px); }
        .ff-hp-reel-card video { width: 100%; height: 100%; object-fit: cover; pointer-events: none; }
        
        .ff-hp-reel-overlay { position: absolute; bottom: 0; left: 0; width: 100%; height: 50%; background: linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%); pointer-events: none; }
        .ff-hp-reel-title { position: absolute; bottom: 20px; left: 15px; right: 15px; color: #ffffff; font-size: 0.95rem; font-weight: 700; line-height: 1.4; text-shadow: 0 2px 4px rgba(0,0,0,0.8); display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; pointer-events: none; }

        .ff-hp-reel-play { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 48px; height: 48px; background: rgba(255,255,255,0.25); backdrop-filter: blur(4px); border-radius: 50%; display: flex; align-items: center; justify-content: center; pointer-events: none; transition: background 0.3s ease; }
        .ff-hp-reel-card:hover .ff-hp-reel-play { background: rgba(90, 32, 223, 0.8); }
        .ff-hp-reel-play svg { width: 20px; height: 20px; fill: #fff; margin-left: 3px; }

        /* Sağ Üstteki Sepet / Ürüne Git İkonu */
        .ff-hp-reel-shop { position: absolute; top: 15px; right: 15px; width: 36px; height: 36px; background: rgba(255,255,255,0.2); backdrop-filter: blur(4px); border-radius: 50%; display: flex; align-items: center; justify-content: center; z-index: 10; transition: all 0.3s ease; }
        .ff-hp-reel-shop:hover { background: #5d0be4; transform: scale(1.1); }
        .ff-hp-reel-shop svg { width: 18px; height: 18px; fill: none; stroke: #fff; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

        /* LIGHTBOX (Büyüyen Video İçin) */
        .ff-reel-lightbox-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.85); z-index: 999999; display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.3s ease; cursor: pointer; }
        .ff-reel-lightbox-overlay.active { opacity: 1; }
        .ff-reel-lightbox-content { background: #000; width: 100%; max-width: 400px; height: 90vh; border-radius: 20px; overflow: hidden; box-shadow: 0 30px 60px rgba(0,0,0,0.4); position: relative; display: flex; align-items: center; justify-content: center; cursor: auto; }
        .ff-reel-lightbox-video { width: 100%; height: 100%; object-fit: cover; }
        
        .ff-reel-lightbox-close { position: absolute; top: 20px; left: 20px; background: rgba(255,255,255,0.2); color: #fff; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 24px; cursor: pointer; transition: all 0.3s ease; z-index: 10; }
        .ff-reel-lightbox-close:hover { background: rgba(255,255,255,0.4); transform: scale(1.1); }

        /* LİGHTBOX İÇİ YENİ BUTON */
        .ff-reel-lightbox-go-btn { position: absolute; top: 20px; right: 20px; background: #5d0be4; color: #fff; padding: 8px 16px; border-radius: 24px; text-decoration: none !important; font-weight: 700; font-size: 0.9rem; display: flex; align-items: center; gap: 6px; z-index: 10; transition: all 0.3s ease; box-shadow: 0 4px 15px rgba(90, 32, 223, 0.4); }
        .ff-reel-lightbox-go-btn:hover { background: #4c1bc0; transform: scale(1.05); color: #fff; }
        .ff-reel-lightbox-go-btn svg { width: 16px; height: 16px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

        @media (max-width: 768px) {
            .ff-hp-reels-section { margin-top: 40px; padding: 0 15px; }
            .ff-hp-reels-header h2 { font-size: 1.6rem; }
            .ff-hp-reel-card { flex: 0 0 220px; height: 390px; }
            .ff-hp-reel-title { font-size: 0.85rem; bottom: 15px; }
            .ff-reel-lightbox-content { max-width: 320px; height: 80vh; }
            .ff-hp-arrow { display: none; } /* Mobilde kaydırmak yeterli, oklara gerek yok */
        }
    `;
    document.head.appendChild(hpStyle);

    // Kapsayıcı HTML
    const hpReelsContainer = document.createElement('div');
    hpReelsContainer.className = 'ff-hp-reels-section';

    let sliderHtml = `
        <div class="ff-hp-reels-header">
            <h2>Ürünlerimizi Yakından İnceleyin</h2>
            <p>Gerçek performans, gerçek deneyim.</p>
        </div>
        <div class="ff-slider-wrapper">
            <button class="ff-hp-arrow prev"><svg viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg></button>
            <div class="ff-hp-reels-slider" id="ffHpSlider">
    `;

    // Kartları Döngü ile Bas (SİYAH EKRAN HİLESİ BURAYA EKLENDİ #t=0.001)
    homeReelsData.forEach(item => {
        let shortTitle = item.title.replace(/Fifine /i, '').replace(/Ampligame /i, '').replace(/AmpliTank /i, '').trim().split(' ')[0];

        sliderHtml += `
            <div class="ff-hp-reel-card" data-video="${item.video}" data-url="${item.url}" data-short="${shortTitle}">
                <video muted playsinline loop preload="metadata">
                    <source src="${item.video}#t=0.001" type="video/mp4">
                </video>
                <div class="ff-hp-reel-overlay"></div>
                <div class="ff-hp-reel-play">
                    <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                </div>
                <a href="${item.url}" target="_blank" class="ff-hp-reel-shop" title="Ürüne Git">
                    <svg viewBox="0 0 24 24"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
                </a>
                <div class="ff-hp-reel-title">${item.title}</div>
            </div>
        `;
    });

    sliderHtml += `
            </div>
            <button class="ff-hp-arrow next"><svg viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg></button>
        </div>
    `;
    
    hpReelsContainer.innerHTML = sliderHtml;
    testimonialWrapper.insertAdjacentElement('beforebegin', hpReelsContainer);

    // ==========================================
    // SLIDER, OKLAR VE AUTOPLAY (OTOMATİK KAYDIRMA)
    // ==========================================
    const slider = document.getElementById('ffHpSlider');
    const prevBtn = hpReelsContainer.querySelector('.ff-hp-arrow.prev');
    const nextBtn = hpReelsContainer.querySelector('.ff-hp-arrow.next');
    
    let isDown = false;
    let startX;
    let scrollLeft;
    let isDragging = false; 
    let autoScrollInterval;
    const cardScrollAmount = 276; 

    nextBtn.addEventListener('click', () => { slider.scrollBy({ left: cardScrollAmount, behavior: 'smooth' }); });
    prevBtn.addEventListener('click', () => { slider.scrollBy({ left: -cardScrollAmount, behavior: 'smooth' }); });

    function startAutoScroll() {
        autoScrollInterval = setInterval(() => {
            if (slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 10) {
                slider.scrollTo({ left: 0, behavior: 'smooth' });
            } else {
                slider.scrollBy({ left: cardScrollAmount, behavior: 'smooth' });
            }
        }, 3000); 
    }

    function stopAutoScroll() { clearInterval(autoScrollInterval); }

    slider.addEventListener('mouseenter', stopAutoScroll);
    slider.addEventListener('mouseleave', startAutoScroll);
    slider.addEventListener('touchstart', stopAutoScroll);
    slider.addEventListener('touchend', startAutoScroll);

    startAutoScroll(); 

    // Mouse İle Sürükle Bırak (Drag)
    slider.addEventListener('mousedown', (e) => {
        isDown = true;
        isDragging = false;
        slider.style.scrollSnapType = 'none'; 
        startX = e.pageX - slider.offsetLeft;
        scrollLeft = slider.scrollLeft;
    });
    slider.addEventListener('mouseleave', () => { isDown = false; slider.style.scrollSnapType = 'x mandatory'; });
    slider.addEventListener('mouseup', () => { isDown = false; slider.style.scrollSnapType = 'x mandatory'; });
    slider.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        isDragging = true; 
        const x = e.pageX - slider.offsetLeft;
        const walk = (x - startX) * 2; 
        slider.scrollLeft = scrollLeft - walk;
    });

    // ==========================================
    // LIGHTBOX (BÜYÜTME VE SES) MANTIĞI
    // ==========================================
    const hpCards = hpReelsContainer.querySelectorAll('.ff-hp-reel-card');
    
    hpCards.forEach(card => {
        card.addEventListener('click', function(e) {
            if (e.target.closest('.ff-hp-reel-shop') || isDragging) return;

            const videoUrl = this.getAttribute('data-video');
            const productUrl = this.getAttribute('data-url');
            const shortTitle = this.getAttribute('data-short');
            
            stopAutoScroll(); 
            openHpLightbox(videoUrl, productUrl, shortTitle);
        });
    });

    function openHpLightbox(videoUrl, productUrl, shortTitle) {
        const overlay = document.createElement('div');
        overlay.className = 'ff-reel-lightbox-overlay';

        const content = document.createElement('div');
        content.className = 'ff-reel-lightbox-content';

        const closeBtn = document.createElement('div');
        closeBtn.className = 'ff-reel-lightbox-close';
        closeBtn.innerHTML = '&times;';
        content.appendChild(closeBtn);

        const goBtn = document.createElement('a');
        goBtn.href = productUrl;
        goBtn.target = '_blank';
        goBtn.className = 'ff-reel-lightbox-go-btn';
        goBtn.innerHTML = `<span>${shortTitle} İncele</span> <svg viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>`;
        content.appendChild(goBtn);

        const lightboxVideo = document.createElement('video');
        lightboxVideo.className = 'ff-reel-lightbox-video';
        lightboxVideo.controls = true; 
        lightboxVideo.playsinline = true; 
        lightboxVideo.autoplay = true; 
        
        const videoSource = document.createElement('source');
        videoSource.src = videoUrl;
        videoSource.type = 'video/mp4';
        lightboxVideo.appendChild(videoSource);
        
        lightboxVideo.muted = false;

        content.appendChild(lightboxVideo);
        overlay.appendChild(content);
        document.body.appendChild(overlay);

        const closeLightbox = () => {
            overlay.classList.remove('active');
            lightboxVideo.pause(); 
            startAutoScroll(); 
            setTimeout(() => { document.body.removeChild(overlay); }, 300);
        };

        closeBtn.addEventListener('click', function(e) {
            e.stopPropagation();
            closeLightbox();
        });

        overlay.addEventListener('click', closeLightbox);
        content.addEventListener('click', function(e) { e.stopPropagation(); });

        setTimeout(() => { overlay.classList.add('active'); }, 10);
    }
});






// trust infolar
// ==========================================
// 6. ÜRÜN BİLGİ İKONLARINI YATAY HİZALAMA (2x2 GRID COMPACT TASARIM)
// ==========================================
(function() {
    // Sadece ürün sayfalarında çalışması için kontrol
    if (!window.location.pathname.includes('/products/')) return;

    // Sayfadaki tüm özellik ikonlarını (Kargo, Garanti, Güvence vb.) bul
    const iconBlocks = document.querySelectorAll('.product-icon-text');
    if (iconBlocks.length < 2) return; // Eğer ikonlar yoksa kodu durdur

    // İkonları sarmalayacak yeni bir Grid kapsayıcısı oluştur
    const gridWrapper = document.createElement('div');
    gridWrapper.className = 'ff-icon-features-grid';
    
    // YENİ CSS: İkonları ve metinleri yan yana (2x2) sıkıştıracak modern görünüm
    const style = document.createElement('style');
    style.innerHTML = `
        .ff-icon-features-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr); /* Yan yana 2 sütun */
            gap: 12px 10px; /* Kutular arası boşluk */
            margin: 15px 0; /* Üstten alttan boşluk */
        }
        .ff-icon-features-grid .product-icon-text {
            margin: 0 !important; /* Temanın varsayılan boşluklarını ez */
            padding: 0 !important;
            display: flex;
            align-items: center; /* İkon ile yazıyı tam ortala */
            font-size: 0.85rem;
            line-height: 1.2;
            color: #374151;
        }
        .ff-icon-features-grid .product-icon-text svg {
            width: 24px !important;
            height: 24px !important;
            margin-right: 8px !important;
            flex-shrink: 0; /* İkonun ezilmesini engelle */
            color: #111827; /* İkon rengi */
        }
        
        /* Mobil Ekranlar İçin Sıkıştırma (Mobilde de 2x2 kalır ama font küçülür) */
        @media (max-width: 768px) {
            .ff-icon-features-grid {
                gap: 10px 8px;
            }
            .ff-icon-features-grid .product-icon-text {
                font-size: 0.75rem;
            }
            .ff-icon-features-grid .product-icon-text svg {
                width: 20px !important;
                height: 20px !important;
                margin-right: 6px !important;
            }
        }
    `;
    document.head.appendChild(style);

    // Yeni ızgarayı, ilk ikonun hemen öncesine yerleştir
    iconBlocks[0].parentNode.insertBefore(gridWrapper, iconBlocks[0]);

    // Dağınık duran tüm ikon bloklarını bu yeni ızgaranın içine taşı
    iconBlocks.forEach(block => {
        gridWrapper.appendChild(block);
    });
})();
// trust infolar







// SEPET CROSS SELL
// ==========================================
// 10. SEPET ÇEKMECESİ (DRAWER) ÇAPRAZ SATIŞ (SÜRÜKLE & BIRAK + SAYFA YENİLEMELİ KESİN ÇÖZÜM)
// ==========================================
(function() {
    // DEV ÇAPRAZ SATIŞ HARİTASI
    const crossSellMap = {
        "fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu": ["fifine-l9-3-metre-profesyonel-altin-kaplama-3-pin-xlr-mikrofon-ses-kablosu", "fifine-ampligame-sc3-ses-efektli-rgb-xlr-usb-oyuncu-ve-yayinci-mikseri", "fifine-bm88-low-profile-profesyonel-masaustu-mikrofon-standi"],
        "fifine-amplitank-tank1-low-cut-filtreli-safkan-xlr-dinamik-studyo-mikrofonu": ["fifine-l9-3-metre-profesyonel-altin-kaplama-3-pin-xlr-mikrofon-ses-kablosu", "fifine-ampligame-sc8-7-1-surround-ses-efektli-rgb-xlr-usb-yayinci-ses-mikseri", "fifine-bm66-premium-agir-yuk-tasiyici-360-masaustu-mikrofon-kolu"],
        "fifine-ampligame-am8-xlr-usb-hibrit-rgb-dinamik-yayinci-mikrofonu": ["fifine-l9-3-metre-profesyonel-altin-kaplama-3-pin-xlr-mikrofon-ses-kablosu", "fifine-ampligame-sc3-ses-efektli-rgb-xlr-usb-oyuncu-ve-yayinci-mikseri", "fifine-ampligame-bm38-rgb-aydinlatmali-profesyonel-boom-arm-mikrofon-standi"],
        "fifine-ampligame-am8prot-rgb-standli-xlr-usb-dinamik-yayinci-mikrofon-seti": ["fifine-l9-3-metre-profesyonel-altin-kaplama-3-pin-xlr-mikrofon-ses-kablosu", "fifine-ampligame-sc3-ses-efektli-rgb-xlr-usb-oyuncu-ve-yayinci-mikseri", "fifine-ampligame-h6-mikrofonlu-7-1-surround-rgb-usb-oyuncu-ve-yayinci-kulakligi"],
        "fifine-ampligame-am8t-standli-xlr-usb-rgb-dinamik-yayinci-mikrofon-seti": ["fifine-l9-3-metre-profesyonel-altin-kaplama-3-pin-xlr-mikrofon-ses-kablosu", "fifine-ampligame-sc3-ses-efektli-rgb-xlr-usb-oyuncu-ve-yayinci-mikseri", "fifine-ampligame-h9-7-1-surround-ses-kartli-mikrofonlu-profesyonel-oyuncu-kulakligi"],
        "fifine-ampligame-am9-rgb-usb-xlr-hibrit-dinamik-oyuncu-ve-yayinci-mikrofonu": ["fifine-l9-3-metre-profesyonel-altin-kaplama-3-pin-xlr-mikrofon-ses-kablosu", "fifine-ampligame-sc8-7-1-surround-ses-efektli-rgb-xlr-usb-yayinci-ses-mikseri", "fifine-bm88-low-profile-profesyonel-masaustu-mikrofon-standi"],
        "fifine-ampligame-sc3-ses-efektli-rgb-xlr-usb-oyuncu-ve-yayinci-mikseri": ["fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu", "fifine-l9-3-metre-profesyonel-altin-kaplama-3-pin-xlr-mikrofon-ses-kablosu", "fifine-ampligame-am8-xlr-usb-hibrit-rgb-dinamik-yayinci-mikrofonu"],
        "fifine-ampligame-sc8-7-1-surround-ses-efektli-rgb-xlr-usb-yayinci-ses-mikseri": ["fifine-amplitank-tank1-low-cut-filtreli-safkan-xlr-dinamik-studyo-mikrofonu", "fifine-l9-3-metre-profesyonel-altin-kaplama-3-pin-xlr-mikrofon-ses-kablosu", "fifine-ampligame-am9-rgb-usb-xlr-hibrit-dinamik-oyuncu-ve-yayinci-mikrofonu"],
        "fifine-ampli1-xlr-ve-enstruman-girisli-usb-studyo-ses-karti": ["fifine-k688-xlr-usb-hibrit-dinamik-studyo-podcast-ve-yayin-mikrofonu", "fifine-l9-3-metre-profesyonel-altin-kaplama-3-pin-xlr-mikrofon-ses-kablosu"],
        "fifine-ampligame-d6-stream-deck-15-tuslu-programlanabilir-yayin-kontrolcusu": ["fifine-ampligame-sc3-ses-efektli-rgb-xlr-usb-oyuncu-ve-yayinci-mikseri", "fifine-ampligame-v3-4k-kayit-rgb-usb-yayinci-capture-card"],
        "fifine-ampligame-a6v-rgb-usb-oyuncu-ve-yayinci-condenser-mikrofonu": ["fifine-ampligame-bm38-rgb-aydinlatmali-profesyonel-boom-arm-mikrofon-standi", "fifine-ampligame-h6-mikrofonlu-7-1-surround-rgb-usb-oyuncu-ve-yayinci-kulakligi"],
        "fifine-ampligame-a8-rgb-usb-podcast-ve-yayinci-mikrofonu": ["fifine-bm88-low-profile-profesyonel-masaustu-mikrofon-standi", "fifine-ampligame-h9-7-1-surround-ses-kartli-mikrofonlu-profesyonel-oyuncu-kulakligi"],
        "fifine-k658-rgb-dinamik-usb-podcast-ve-bilgisayar-yayinci-mikrofonu": ["fifine-bm88-low-profile-profesyonel-masaustu-mikrofon-standi", "fifine-ampligame-h19-7-1-surround-eq-modlu-rgb-usb-oyuncu-kulakligi"],
        "fifine-ampligame-am6-rgb-usb-oyuncu-mikrofonu": ["fifine-bm88-low-profile-profesyonel-masaustu-mikrofon-standi", "fifine-ampligame-h16-7-1-surround-usb-3-5mm-rgb-oyuncu-kulakligi"],
        "fifine-amplitank-profile3-condenser-usb-yayinci-mikrofonu": ["fifine-bm66-premium-agir-yuk-tasiyici-360-masaustu-mikrofon-kolu", "fifine-ampligame-h8-studyo-referans-oyuncu-kulakligi"],
        "fifine-k669b-studyo-ve-yayinci-usb-condenser-bilgisayar-mikrofonu": ["fifine-bm63-profesyonel-masa-tipi-yayli-akrobat-mikrofon-standi", "fifine-pf1-cift-katmanli-esnek-boyunlu-profesyonel-mikrofon-pop-filtre"],
        "fifine-ampligame-h6-mikrofonlu-7-1-surround-rgb-usb-oyuncu-ve-yayinci-kulakligi": ["fifine-ampligame-a6v-rgb-usb-oyuncu-ve-yayinci-condenser-mikrofonu", "fifine-ampligame-a20-rgb-2-0-stereo-usb-ve-aux-girisli-oyuncu-bilgisayar-hoparloru"],
        "fifine-ampligame-h9-7-1-surround-ses-kartli-mikrofonlu-profesyonel-oyuncu-kulakligi": ["fifine-ampligame-a8-rgb-usb-podcast-ve-yayinci-mikrofonu", "fifine-ampligame-a22-rgb-2-1-subwooferli-usb-ve-bluetooth-oyuncu-bilgisayar-hoparloru"],
        "fifine-ampligame-h13-7-1-surround-rgb-usb-oyuncu-kulakligi": ["fifine-ampligame-am6-rgb-usb-oyuncu-mikrofonu"],
        "fifine-ampligame-h19-7-1-surround-eq-modlu-rgb-usb-oyuncu-kulakligi": ["fifine-k658-rgb-dinamik-usb-podcast-ve-bilgisayar-yayinci-mikrofonu"],
        "fifine-x3-aktif-gurultu-engelleyici-anc-mikrofonlu-kablosuz-bluetooth-kulak-ustu-kulaklik": ["fifine-ampligame-a8-rgb-usb-podcast-ve-yayinci-mikrofonu"],
        "fifine-bm88-low-profile-profesyonel-masaustu-mikrofon-standi": ["fifine-pf1-cift-katmanli-esnek-boyunlu-profesyonel-mikrofon-pop-filtre", "fifine-l9-3-metre-profesyonel-altin-kaplama-3-pin-xlr-mikrofon-ses-kablosu"],
        "fifine-bm66-premium-agir-yuk-tasiyici-360-masaustu-mikrofon-kolu": ["fifine-sk1-titresim-onleyici-shock-mount-mikrofon-tutucu"],
        "fifine-ampligame-bm38-rgb-aydinlatmali-profesyonel-boom-arm-mikrofon-standi": ["fifine-ampligame-a6-a6v-a6t-serisi-uyumlu-yedek-pop-filtre"]
    };

    const style = document.createElement('style');
    style.innerHTML = `
        .ff-drawer-upsell { padding: 15px 15px 5px 15px; border-top: 1px solid rgba(255,255,255,0.08); background: transparent; margin-top: auto; }
        .ff-drawer-upsell-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
        .ff-drawer-upsell-title { font-size: 0.95rem; font-weight: 700; color: #ffffff; display: flex; align-items: center; gap: 8px; margin: 0; }
        .ff-drawer-upsell-title svg { color: #5A20DF; }
        
        .ff-drawer-upsell-nav { display: flex; gap: 6px; }
        .ff-nav-btn { background: #1A1A1A; color: #fff; border: 1px solid #2a2a2a; border-radius: 6px; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; }
        .ff-nav-btn:hover:not(:disabled) { background: #5A20DF; border-color: #5A20DF; }
        .ff-nav-btn:disabled { opacity: 0.3; cursor: default; }

        .ff-drawer-upsell-list { 
            display: flex; flex-wrap: nowrap !important; width: 100% !important; gap: 10px; 
            overflow-x: auto; padding-bottom: 10px; scrollbar-width: none; 
            scroll-snap-type: x mandatory; cursor: grab; scroll-behavior: smooth;
        }
        .ff-drawer-upsell-list::-webkit-scrollbar { display: none; } 
        .ff-drawer-upsell-list.is-dragging { cursor: grabbing; scroll-snap-type: none; scroll-behavior: auto; }
        
        .ff-drawer-upsell-card {
            background: #1A1A1A; border: 1px solid #2a2a2a; border-radius: 10px; padding: 10px;
            display: flex; align-items: center; gap: 12px; width: 260px !important; max-width: 82vw; 
            flex: 0 0 auto !important; scroll-snap-align: start; box-sizing: border-box; user-select: none;
        }
        .ff-drawer-upsell-img { width: 52px; height: 52px; min-width: 52px; object-fit: contain; border-radius: 6px; background: #fff; padding: 3px; pointer-events: none; }
        .ff-drawer-upsell-info { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 3px; pointer-events: none; }
        .ff-drawer-upsell-name { font-size: 0.8rem; font-weight: 600; color: #e5e7eb; line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis; white-space: normal; }
        .ff-drawer-upsell-price { font-size: 0.85rem; font-weight: 700; color: #ffffff; }
        .ff-drawer-upsell-btn {
            background: #5A20DF; color: #ffffff; width: 34px; height: 34px; min-width: 34px; 
            border-radius: 8px; border: none; display: flex; align-items: center; justify-content: center;
            cursor: pointer; transition: all 0.2s; font-size: 20px; font-weight: bold;
        }
        .ff-drawer-upsell-btn:hover { background: #4c1bc0; transform: scale(1.05); }
        .ff-drawer-upsell-btn.loading { opacity: 0.7; pointer-events: none; }
    `;
    document.head.appendChild(style);

    function updateDrawerCrossSell() {
        fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
            const existingUpsell = document.querySelector('.ff-drawer-upsell');
            if (existingUpsell) existingUpsell.remove();

            if (cart.items.length === 0) return;

            const cartHandles = cart.items.map(item => item.handle);
            let recommendedHandles = [];

            cartHandles.forEach(handle => {
                if (crossSellMap[handle]) {
                    recommendedHandles = recommendedHandles.concat(crossSellMap[handle]);
                }
            });

            recommendedHandles = recommendedHandles.filter(handle => !cartHandles.includes(handle));
            recommendedHandles = [...new Set(recommendedHandles)].slice(0, 4);

            if (recommendedHandles.length === 0) return;

            const cartFooter = document.querySelector('.ff-hd-cart__foot');
            if (!cartFooter) return;

            const wrapper = document.createElement('div');
            wrapper.className = 'ff-drawer-upsell';
            
            wrapper.innerHTML = `
                <div class="ff-drawer-upsell-header">
                    <div class="ff-drawer-upsell-title">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg> 
                        Tamamlayıcı Ürünler
                    </div>
                    <div class="ff-drawer-upsell-nav" style="display:none;">
                        <button class="ff-nav-btn prev" disabled><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M15 18l-6-6 6-6"/></svg></button>
                        <button class="ff-nav-btn next"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 18l6-6-6-6"/></svg></button>
                    </div>
                </div>
                <div class="ff-drawer-upsell-list"></div>
            `;
            
            const listContainer = wrapper.querySelector('.ff-drawer-upsell-list');
            const navContainer = wrapper.querySelector('.ff-drawer-upsell-nav');

            Promise.all(recommendedHandles.map(handle => 
                fetch('/products/' + handle + '.js').then(res => res.json())
            )).then(products => {
                let addedCount = 0;

                products.forEach(p => {
                    // STOK KONTROLÜ
                    if (!p.available) return; 
                    const availableVariant = p.variants.find(v => v.available);
                    if (!availableVariant) return;

                    addedCount++;
                    let cleanTitle = p.title.replace(/Fifine /i, '').replace(/Ampligame /i, '').replace(/AmpliTank /i, '').replace(/3 Metre Profesyonel Altın Kaplama 3-Pin XLR Mikrofon Ses Kablosu/i, 'L9 XLR Ses Kablosu').split('-')[0].trim();
                    let priceStr = '₺' + (p.price / 100).toLocaleString('tr-TR', {minimumFractionDigits: 2});

                    let cardHTML = `
                        <div class="ff-drawer-upsell-card">
                            <img class="ff-drawer-upsell-img" src="${p.featured_image}" alt="${cleanTitle}" draggable="false">
                            <div class="ff-drawer-upsell-info">
                                <span class="ff-drawer-upsell-name" title="${cleanTitle}">${cleanTitle}</span>
                                <span class="ff-drawer-upsell-price">${priceStr}</span>
                            </div>
                            <button class="ff-drawer-upsell-btn" data-variant-id="${availableVariant.id}" title="Sepete Ekle">+</button>
                        </div>
                    `;
                    listContainer.insertAdjacentHTML('beforeend', cardHTML);
                });

                if (addedCount === 0) return;

                if (addedCount > 1) {
                    navContainer.style.display = 'flex';
                }

                cartFooter.insertAdjacentElement('beforebegin', wrapper);

                // --- 1. OK TUŞLARI ---
                const prevBtn = wrapper.querySelector('.prev');
                const nextBtn = wrapper.querySelector('.next');

                const updateNavStates = () => {
                    prevBtn.disabled = listContainer.scrollLeft <= 0;
                    nextBtn.disabled = listContainer.scrollLeft + listContainer.clientWidth >= listContainer.scrollWidth - 2;
                };

                listContainer.addEventListener('scroll', updateNavStates);
                setTimeout(updateNavStates, 150);

                prevBtn.addEventListener('click', () => { listContainer.scrollBy({ left: -260, behavior: 'smooth' }); });
                nextBtn.addEventListener('click', () => { listContainer.scrollBy({ left: 260, behavior: 'smooth' }); });

                // --- 2. SÜRÜKLE BIRAK (DRAG) ---
                let isDown = false;
                let startX;
                let scrollLeft;

                listContainer.addEventListener('mousedown', (e) => {
                    isDown = true;
                    listContainer.classList.add('is-dragging');
                    startX = e.pageX - listContainer.offsetLeft;
                    scrollLeft = listContainer.scrollLeft;
                });
                listContainer.addEventListener('mouseleave', () => { isDown = false; listContainer.classList.remove('is-dragging'); });
                listContainer.addEventListener('mouseup', () => { isDown = false; listContainer.classList.remove('is-dragging'); });
                listContainer.addEventListener('mousemove', (e) => {
                    if (!isDown) return;
                    e.preventDefault();
                    const x = e.pageX - listContainer.offsetLeft;
                    const walk = (x - startX) * 1.5; 
                    listContainer.scrollLeft = scrollLeft - walk;
                });

                // --- 3. KESİN ÇÖZÜM: SAYFAYI YENİLEYEREK SEPETE EKLEME ---
                wrapper.querySelectorAll('.ff-drawer-upsell-btn').forEach(btn => {
                    btn.addEventListener('click', function(e) {
                        e.preventDefault();
                        const btnEl = this;
                        btnEl.classList.add('loading');
                        btnEl.innerHTML = '...'; 

                        let variantId = btnEl.getAttribute('data-variant-id');
                        let formData = { 'items': [{ 'id': variantId, 'quantity': 1 }] };

                        fetch(window.Shopify.routes.root + 'cart/add.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(formData)
                        })
                        .then(response => {
                            if (response.ok) {
                                // Ekledikten sonra doğrudan sayfayı yenile. En sağlıklı ve %100 çalışan yol.
                                window.location.reload(); 
                            } else {
                                btnEl.classList.remove('loading');
                                btnEl.innerHTML = '+';
                            }
                        })
                        .catch(error => {
                            console.error('Error:', error);
                            btnEl.classList.remove('loading');
                            btnEl.innerHTML = '+';
                        });
                    });
                });
            });
        });
    }

    updateDrawerCrossSell();

    const targetNode = document.querySelector('.ff-hd-cart__body');
    if (targetNode && !window.ffCrossSellObserverAdded) {
        window.ffCrossSellObserverAdded = true;
        const config = { childList: true, subtree: true };
        const callback = function(mutationsList, observer) {
            clearTimeout(window.ffCartUpdateTimer);
            window.ffCartUpdateTimer = setTimeout(() => {
                updateDrawerCrossSell();
            }, 300);
        };
        const observer = new MutationObserver(callback);
        observer.observe(targetNode, config);
    }
})();
// SEPET CROSS SELL



// ==========================================
// FOOTER ÖDEME VE GÜVENLİK İKONLARI (JS İLE ENJEKTE)
// ==========================================
(function() {
    // 1. Hedef: Footer'daki ödeme listesi alanını bul
    const footerPaymentsContainer = document.querySelector('.footer-payments ul.list-unstyled') || document.querySelector('.footer-payments');
    
    // Eğer footer'da bu alan yoksa kodu durdur
    if (!footerPaymentsContainer) return; 

    // 2. Sadece istenen 3 logonun URL'leri
    const badgeUrls = [
        "https://cdn.shopify.com/s/files/1/0757/6053/3556/files/mastercard.png?v=1777533900", 
        "https://cdn.shopify.com/s/files/1/0757/6053/3556/files/visa.png?v=1777533900",
        "https://cdn.shopify.com/s/files/1/0757/6053/3556/files/iyzico-logo.webp?v=1778243999"
    ];

    // 3. Container stil ayarları (Ortalama, mobilde alt satıra inme ve boşluklar)
    footerPaymentsContainer.style.display = 'flex';
    footerPaymentsContainer.style.flexWrap = 'wrap';
    footerPaymentsContainer.style.justifyContent = 'center';
    footerPaymentsContainer.style.alignItems = 'center';
    footerPaymentsContainer.style.gap = '8px';
    footerPaymentsContainer.style.padding = '0';
    footerPaymentsContainer.style.margin = '15px 0 0 0';

    // (Opsiyonel) İçerisi zaten Shopify tarafından doldurulmuşsa önce temizle
    footerPaymentsContainer.innerHTML = ''; 

    // 4. Logoları oluştur ve kapsayıcıya ekle
    badgeUrls.forEach(url => {
        // Her logo için bir <li> elementi oluşturuyoruz (HTML yapısına uygun olsun diye)
        const li = document.createElement('li');
        li.style.listStyle = 'none'; // Madde imlerini kaldır
        
        const img = document.createElement('img');
        img.src = url;
        img.alt = "Güvenli Ödeme Yöntemi";
        
        // --- ÜRÜN SAYFASI İLE BİREBİR AYNI KUTU STİLLERİ ---
        img.style.height = "40px"; 
        img.style.width = "auto";
        img.style.objectFit = "contain";
        img.style.border = "1px solid #d2d0cc";
        img.style.borderRadius = "4px";
        img.style.backgroundColor = "#ffffff";
        img.style.padding = "8px 12px"; 
        img.style.boxSizing = "border-box";
        img.style.display = "block";

        // Resmi <li> içine, <li>'yi de ana container'a ekle
        li.appendChild(img);
        footerPaymentsContainer.appendChild(li);
    });
})();